import {
  basePogWalk,
  pairKeyOf,
  pairPriorities,
  placementBayOf,
  walkOrderedByCdt0,
} from "./basePogWalk";
import {
  allocateZoneBays,
  CDT0_NAME_BUDGET,
  clearsStackFloor,
  DEFAULT_COLUMN_ATTR,
  deriveBandRules,
  deriveZoneShareRules,
  deriveStrata,
  materializePartitionRules,
  materializeRestrictionRows,
  namedSectionOf,
  sectionNameOf,
  withRowHomeStrata,
  type BayPartitionResult,
  type FenceOptions,
  type FixtureSideInput,
  type PartitionRules,
  type ReplicaRestrictionRow,
  type SectionNamingDraft,
  type StrataResult,
  type StratumPlan,
  zonesFromBays,
  zonesFromShippedOrder,
} from "./bayPartition";
import { uniformFixture } from "./storeFixture";
import { runBayWidthOf } from "./bayShare";
import { measureBlockingOf, VERTICAL_SCORE, type BlockingScope } from "./blockOrientation";
import { buildLayout } from "./buildLayout";
import { collapse4, compoundPriorities, sanitizeCdtValue } from "./collapse4";
import type { LevelRole } from "./cdtLevelPlan";
import { NONE_VALUE, inferTree, type TreeNode } from "./levelInference";
import { SIZE_LIKE_SHARE, sizeLikeShare } from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { deriveRestrictionPlacement } from "./restrictionPlacement";
import { layoutPlacementsOf, shelfRankSpaceOf } from "./shelfRankSpace";
import type {
  RuleCarrier,
  RuleCarve,
  RuleNode,
  RulePool,
  RuleSet,
  RuleSkuFacts,
  RuleTreeNode,
} from "./storeRules";
import {
  FinalizeResult,
  GeneratedLayout,
  PATH_SEPARATOR,
  SkuAssignment,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  lengthLabel,
  type LengthUnit,
  isHorizontalWall,
  isPlaceholderValue,
} from "./studioTypes";
import { categoryLengthUnitOf } from "./studioLengthUnit";

export const finalizeCdts = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  authoritativeLayout?: GeneratedLayout,
): FinalizeResult => {
  const contractLayout = buildLayout(items, draft, runs);
  const deepLayout = authoritativeLayout ?? contractLayout;
  const collapse = collapse4(items, draft, runs, deepLayout, contractLayout.walkOrder);
  const priorities = compoundPriorities(collapse.contract, collapse.priorityWalk);
  const positionedItems = items.map((item) => ({
    ...item,
    placements: layoutPlacementsOf(item, deepLayout),
  }));

  const priorityOf = (values: readonly string[], depth: number) =>
    priorities.get(values.slice(0, depth + 1).join(PATH_SEPARATOR)) ?? 0;

  const assignments: SkuAssignment[] = items.map((item) => {
    const values = collapse.contract.itemValues[item.skuId] ?? ["", "", "", ""];
    return {
      skuId: item.skuId,
      upc: item.upc,
      name: item.name,
      cdt0: values[0] ?? "",
      cdt1: values[1] ?? "",
      cdt2: values[2] ?? "",
      cdt3: values[3] ?? "",
      cdt1Priority: priorityOf(values, 1),
      cdt2Priority: priorityOf(values, 2),
      cdt3Priority: priorityOf(values, 3),
    };
  });

  // Do not send stale restrictions: Pogger would match no candidate and empty every addressed cell.
  const shippedVariants = new Set(
    Object.values(collapse.contract.itemValues)
      .map((values) => values[0] ?? "")
      .filter((value) => value !== ""),
  );

  // Bind to the first SKU's compound key; unmeasurable geometry stays null, never "*".
  const measured = draft.restrictions.map((restriction) => {
    const firstSku = restriction.skuIds[0];
    const values = firstSku ? collapse.contract.itemValues[firstSku] : undefined;
    return {
      ...restriction,
      collapsedKey: values ? values.join(PATH_SEPARATOR) : null,
      placement: deriveRestrictionPlacement(restriction.skuIds, positionedItems, runs),
    };
  });
  const restrictions: typeof measured = [];
  const staleRestrictions: { restriction: (typeof measured)[number]; reason: string }[] = [];
  for (const restriction of measured) {
    const named = restriction.cdtPath[restriction.cdtPath.length - 1]?.trim() ?? "";
    if (named !== "" && !shippedVariants.has(named)) {
      staleRestrictions.push({
        restriction,
        reason:
          `"${named}" is no longer a value this tree ships at CDT0, so the row would ` +
          "match no candidate and empty every cell it names",
      });
      continue;
    }
    restrictions.push(restriction);
  }

  return {
    contract: collapse.contract,
    verification: collapse.verification,
    assignments,
    restrictions,
    staleRestrictions,
  };
};

export interface ReplicaEmissionOptions {
  sectionAttr?: string;

  /** Optional for base-POG emission; shipping passes it so promoted zones keep their names. */
  draft?: SectionNamingDraft & Partial<Pick<StudioDraft, "spine">>;

  /** The root order shipped by the tree; omitted when measuring a base POG alone. */
  cdt0Order?: readonly string[];

  rulings?: Readonly<Record<string, LevelRole>>;

  carveByRowAttribute?: boolean;
  /** How the base-wall fences are drawn: per bay (ragged edges kept) and
   *  with shared seam bays (the base's own spill stays legal). Both default
   *  off; see `FenceOptions`. */
  fences?: FenceOptions;
  /** Carve a brand that stands on a proper subset of its section's rows in
   *  every bay it occupies (a tail-bay slab, a store brand under another
   *  brand) into a cdt0 sub-stratum fenced to those rows. In vertical mode a
   *  cdt1 column is full height and cdt2 cannot pin a shelf, so a restriction
   *  row is the only carrier for the fact. Default off. */
  carveStackedBrands?: boolean;
  /** How big a stacked brand has to be before `carveStackedBrands` gives it a
   *  variant of its own, and how many slabs one section may shed. Every extra
   *  cdt0 is a rectangle in the mudmap solve and every SKU it takes is a SKU
   *  the residual parent no longer has, so the floor is deliberately high:
   *  a slab is 5 SKUs OR a full bay of shelf width, a section sheds at most 2,
   *  and a slab is folded back whenever the residual it leaves would itself be
   *  under that floor. Read only when `carveStackedBrands` is on. */
  stackedBrandFloor?: {
    /** SKUs a slab must hold. Default 5. */
    minSkus?: number;
    /** Shelf width a slab must hold, in bays. Default 1 (a whole bay). */
    minBayShare?: number;
    /** Slabs one cdt0 may shed, widest first. Default 2. */
    maxPerSection?: number;
  };
  /** Accept partial cakes in `deriveStrata` (see `StrataOptions`). Default off. */
  partialCakes?: boolean;
  /** Shared row partitions under cdt1 (§9, step 2b) conform on SHAPE rather
   *  than exact rows: a numeric seed may have three or more tiers (small /
   *  mega / bulk), a brand conforms when its own thresholds stack its groups
   *  top to bottom touching the seed's rows, and each side's sub-stratum takes
   *  the union of the conforming brands' rows (the per-bay fence keeps every
   *  brand's own ragged edge). Default off: two sides, exact row sets. */
  relaxedRowPartitions?: boolean;
  /** The category's unit, for the advisories' words only; absent, `categoryLengthUnitOf`'s
   *  default. */
  lengthUnit?: LengthUnit;
  /** "Keep each brand's size rows as the base shows them." A row partition
   *  only ONE node of a cdt0 reads (one sub-brand's small packs on the top
   *  shelf, its large ones below) is carved the way a shared one is — one
   *  cdt0 sub-stratum per row side, the stratum's other nodes joining by the
   *  row their items stand on — when it is clean: the split reads vertical,
   *  every value of it stands on its own side's rows, and the partition
   *  (with the items of the other nodes that agree with it) holds at least
   *  80% of the cdt0's width. A node whose tree opens on something else is
   *  read on the draft's vertical levels too. Unclean partitions stay
   *  folded into cdt3 (shipped: false). Default on. */
  keepBrandSizeRows?: boolean;
  /** Zone to bays from the materializer's fixture side run against the base's own
   *  fixture, rather than step 1's merged zones — a merged wall is one zone and
   *  emits no partition at all. Default off. */
  zonesFromFixtureSide?: boolean;
  /** Emit strata and row homes in the Studio's own row ranks (pegboard hang lines kept) rather
   *  than shelf ranks: only for `adoptMeasuredBands`, whose strata the Studio replays. */
  keepRowRanks?: boolean;
}

export interface ReplicaTreeItem {
  cdt0: string;
  cdt1: string;
  cdt2: string;
  cdt3: string;
  cdt1_priority: number;
  cdt2_priority: number;
  cdt3_priority: number;

  segment: string;

  walk_rank: number;

  path: readonly ReplicaPathStep[];
}

export interface ReplicaPathStep {
  attr: string;

  value: string;

  carrier: "rowHome" | "lossy" | "cdt2" | "cdt3" | "seq";

  part: string | null;
}

export interface ReplicaRowHome {
  cdt0: string;
  cdt1: string;
  attr: string;
  value: string;
  shelves: readonly number[];
  skuIds: readonly string[];

  shipped: boolean;
  /** Name suffix override for the carved sub-stratum (`RowHomeRule.label`). */
  label?: string;
}

export interface ReplicaPool {
  cdt0: string;

  cdt1: string;

  host?: string;
  members: readonly {
    cdt1: string;
    skuIds: readonly string[];

    widthShare: number;
    reason: string;
  }[];
}

export interface ReplicaColumnViability {
  cdt0: string;
  cdt1: string;
  items: number;
  widthCm: number;

  widthShare: number;

  salesShare: number;
  subFloor: boolean;

  exempt: boolean;

  poolable: boolean;
}

export interface ReplicaFinding {
  severity: "error" | "warning";
  check: string;
  message: string;
}

export interface ReplicaOrderingRuleReport {
  cdt0: string;
  cdt1: string;
  cdt2: string;

  cdt2Attr: string | null;
  block_order: number;
  rule: string;
  attr: string | null;
  direction: string;
  labels: readonly string[];
  pinnedLast: readonly string[];
}

export interface ReplicaEmission {
  tree: {
    items: Readonly<Record<string, ReplicaTreeItem>>;
    cdt0Order: readonly string[];

    segments: readonly { cdt0: string; cdt1: string; segment: string; priority: number }[];
  };
  restrictions: {
    rules: {
      partition: PartitionRules;
      anchorRule: string;
    };

    rows: readonly ReplicaRestrictionRow[];
    anchors: readonly ReplicaRestrictionRow[];
    partition: BayPartitionResult;
    rowStrata: Readonly<Record<string, readonly number[]>>;
    bay1Anchors: Readonly<Record<string, readonly number[]>>;

    zoneSeams: readonly { leftRun: number; rightRun: number }[];

    rowHomeStrata: readonly StratumPlan[];
  };

  pins: Readonly<
    Record<string, { min_facings: number; max_facings: number; min_days_of_supply: number }>
  >;
  orderingRules: readonly ReplicaOrderingRuleReport[];

  rowHomes: readonly ReplicaRowHome[];

  pools: Readonly<Record<string, ReplicaPool>>;

  poolViability: readonly ReplicaColumnViability[];

  rules: RuleSet;
  advisories: readonly string[];
  findings: readonly ReplicaFinding[];
}

export const resolveAttrValue = (item: StudioItem, attr: string): string =>
  resolveAttrValueOver(item.attrs, attr);

export const resolveAttrValueOver = (
  attrs: Readonly<Record<string, unknown>>,
  attr: string,
): string => {
  const cut = attr.indexOf(" >= ");
  if (cut >= 0) {
    const base = attr.slice(0, cut);
    const threshold = parseFloat(attr.slice(cut + 4));
    const raw = String(attrs[base] ?? "");
    const magnitude = isPlaceholderValue(raw) ? null : raw.match(/[\d.]+/);
    const number = magnitude ? parseFloat(magnitude[0]) : NaN;
    if (Number.isNaN(number)) return NONE_VALUE;
    // Floating-point noise must not move an item across its measured cut.
    return number >= threshold - 1e-6 ? `>= ${attr.slice(cut + 4)}` : `< ${attr.slice(cut + 4)}`;
  }
  const raw = String(attrs[attr] ?? "");
  return isPlaceholderValue(raw) ? NONE_VALUE : raw;
};

export const composeLabel = (parts: readonly string[], fallback: string): string => {
  const live = parts.filter((value, index) => value !== NONE_VALUE && value !== parts[index - 1]);
  return sanitizeCdtValue(live.length > 0 ? live.join(" — ") : fallback);
};

export const emitReplicaRequest = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  options: ReplicaEmissionOptions = {},
): ReplicaEmission => {
  const sectionAttr = options.sectionAttr ?? "variant";
  const draft = options.draft;
  const rulings = options.rulings ?? {};
  // Row-based CDT0 carves are opt-in because they prevent movement between shelves.
  const carveByRowAttribute = options.carveByRowAttribute ?? false;
  const fences: FenceOptions = options.fences ?? {};
  const carveStackedBrands = options.carveStackedBrands ?? false;
  const stackedBrandFloor = {
    minSkus: options.stackedBrandFloor?.minSkus ?? 5,
    minBayShare: options.stackedBrandFloor?.minBayShare ?? 1,
    maxPerSection: options.stackedBrandFloor?.maxPerSection ?? 2,
  };
  const partialCakes = options.partialCakes ?? false;
  const relaxedRowPartitions = options.relaxedRowPartitions ?? false;
  const lengthUnit = options.lengthUnit ?? categoryLengthUnitOf();
  const keepBrandSizeRows = options.keepBrandSizeRows ?? true;
  const zonesFromFixtureSide = options.zonesFromFixtureSide ?? false;
  /** Sequence cdt0s by where they START (bay-major clusters, nested bands by
   *  their starts, mid-span bands unanchored) only under an opt-in flag, so
   *  the default emission stays develop's byte for byte. */
  const startOrdered =
    carveStackedBrands ||
    partialCakes ||
    relaxedRowPartitions ||
    zonesFromFixtureSide ||
    !!fences.perBay ||
    !!fences.sharedSeams;
  const advisories: string[] = [];
  const findings: ReplicaFinding[] = [];
  const warn = (check: string, message: string) =>
    findings.push({ severity: "warning", check, message });
  const fail = (check: string, message: string) =>
    findings.push({ severity: "error", check, message });
  const shelfSpace = shelfRankSpaceOf(runs);
  const emitted = options.keepRowRanks ? shelfRankSpaceOf([]) : shelfSpace;

  let walk = basePogWalk(items, runs);
  /* A horizontally blocked wall is partitioned by its own bays: each section stays in the
     bay it stands in. Otherwise the tree's section order partitions the wall when the studio
     ships one; the base pog's layout only when it does not. */
  const bayZones = isHorizontalWall(draft)
    ? zonesFromBays(items, sectionAttr, options.cdt0Order ?? [], runs, { draft })
    : null;
  const shippedZones =
    bayZones ??
    (options.cdt0Order?.length
      ? zonesFromShippedOrder(items, sectionAttr, options.cdt0Order, {
          draft,
          maxZones: runs[0]?.bays ?? 0,
        })
      : null);
  const partitionRules: PartitionRules =
    shippedZones ?? deriveZoneShareRules(items, sectionAttr, runs.length, runs[0], { lengthUnit });
  const partition: BayPartitionResult = materializePartitionRules(partitionRules, {
    // Bay zones number the bays across every run; the other partitions read the first run.
    totalBays: bayZones
      ? runs.reduce((sum, run) => sum + Math.max(run.bays, 1), 0)
      : (runs[0]?.bays ?? 0),
  });
  // `let`: step 2b refines this with the tree's row partitions (§9).
  let strataResult: StrataResult = deriveStrata(items, runs, sectionAttr, partitionRules, draft, {
    partialCakes,
    lengthUnit,
    // One floor for every carve that multiplies variants; `deriveStrata` reads
    // it only when `partialCakes` is on (see `StrataOptions.bandFloor`).
    bandFloor: stackedBrandFloor,
  });
  for (const warning of [...partitionRules.warnings, ...strataResult.warnings]) {
    advisories.push(warning);
  }
  // cdt0 is named through the draft so the rule set spells the zones the tree ships;
  // the section stays raw, so a promotion makes a new cdt0 and never a new section.
  const cdt0Of = (item: StudioItem): string =>
    strataResult.cdt0Of.get(item.skuId) ?? namedSectionOf(item, sectionAttr, draft);
  const sectionOf = (item: StudioItem): string => sectionNameOf(item, sectionAttr);
  if (options.cdt0Order?.length) {
    const cdt0OfSku = new Map(items.map((item) => [item.skuId, cdt0Of(item)]));
    walk = walkOrderedByCdt0(walk, (skuId) => cdt0OfSku.get(skuId) ?? "", options.cdt0Order);
  }

  const byCdt0 = new Map<string, StudioItem[]>();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    (byCdt0.get(cdt0) ?? byCdt0.set(cdt0, []).get(cdt0)!).push(item);
  }

  const attrsSeen = new Set<string>();
  for (const item of items) {
    for (const [attr, value] of Object.entries(item.attrs)) {
      if (attr === sectionAttr || attr === "subCategory") continue;
      if (!isPlaceholderValue(value)) attrsSeen.add(attr);
    }
  }

  // Item-master attributes must win; hierarchy-derived columns would make the tree self-referential.
  const overlayOrder: string[] = [];
  for (const item of items) {
    for (const attr of item.overlayAttrs ?? []) {
      if (!overlayOrder.includes(attr)) overlayOrder.push(attr);
    }
  }
  const candidates =
    overlayOrder.length > 0
      ? overlayOrder.filter((attr) => attrsSeen.has(attr))
      : [...attrsSeen].sort();

  const IDENTITY_LIKE_SHARE = 0.4;
  const identityLikeShareOf = (attr: string): number => {
    const values = items
      .map((item) => item.attrs[attr] ?? null)
      .map((value) => String(value ?? ""));
    const live = values.filter((value) => !isPlaceholderValue(value));
    if (live.length === 0) return 0;
    return new Set(live).size / live.length;
  };
  // Size and identity-like fields cannot form stable merchandise columns.
  const columnCandidates = candidates.filter(
    (attr) =>
      sizeLikeShare(items.map((item) => item.attrs[attr] ?? null)) < SIZE_LIKE_SHARE &&
      identityLikeShareOf(attr) < IDENTITY_LIKE_SHARE,
  );
  const branchScopesOf = (branchItems: readonly StudioItem[]): BlockingScope[] => {
    const byRun = new Map<number, StudioItem[]>();
    for (const item of branchItems) {
      (byRun.get(item.run) ?? byRun.set(item.run, []).get(item.run)!).push(item);
    }
    return [...byRun.entries()].map(([run, scopeItems]) => ({ run, path: [], items: scopeItems }));
  };

  const allScopes = [...byCdt0.values()].flatMap(branchScopesOf);
  const planScores = new Map(
    columnCandidates.map((attr) => [attr, measureBlockingOf(items, attr, runs, allScopes).score]),
  );
  const planColumn = [...planScores.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const BRANCH_COLUMN_MARGIN = 0.15;

  const cdt1AttrOf = new Map<string, string>();

  const scoresOfCdt0 = new Map<string, ReadonlyMap<string, number>>();
  for (const [cdt0, branchItems] of byCdt0) {
    const scopes = branchScopesOf(branchItems);
    const ruledHere = (attr: string): LevelRole | undefined =>
      rulings[`${cdt0} / ${attr}`] ?? rulings[attr];

    // A promotion band already owns this vertical tier and cannot split it again.
    const bandAttr = strataResult.strata.find((entry) => entry.cdt0 === cdt0)?.bandAttr ?? null;
    const usable = columnCandidates.filter((attr) => {
      if (attr === bandAttr) return false;
      if (ruledHere(attr) === "horizontal") return false;
      const values = new Set(
        branchItems
          .map((item) => String(item.attrs[attr] ?? ""))
          .filter((value) => !isPlaceholderValue(value)),
      );
      return values.size > 1;
    });
    if (usable.length === 0) {
      cdt1AttrOf.set(cdt0, "");
      warn("cdt1-empty", `"${cdt0}" has nothing left that splits; cdt1 ships blank there.`);
      continue;
    }
    const ruled = usable.find((attr) => ruledHere(attr) === "vertical");
    const scores = new Map(
      usable.map((attr) => [attr, measureBlockingOf(items, attr, runs, scopes).score]),
    );
    const best = [...scores.entries()].sort((a, b) => b[1] - a[1])[0][0];
    const planUsable = planColumn && usable.includes(planColumn) ? planColumn : undefined;
    const measuredColumn =
      !planUsable || (scores.get(best) ?? 0) - (scores.get(planUsable) ?? 0) >= BRANCH_COLUMN_MARGIN
        ? best
        : planUsable;
    const column = ruled ?? measuredColumn;
    cdt1AttrOf.set(cdt0, column);
    scoresOfCdt0.set(cdt0, scores);
    if ((scores.get(column) ?? 0) < VERTICAL_SCORE) {
      warn(
        "cdt1-weak",
        `"${cdt0}" has no clearly columnar attribute; "${column}" leads at ${(scores.get(column) ?? 0).toFixed(2)}.`,
      );
    }
  }

  const sectionOfCdt0 = new Map(strataResult.strata.map((entry) => [entry.cdt0, entry.section]));
  const byFamily = new Map<string, string[]>();
  for (const cdt0 of byCdt0.keys()) {
    const family = sectionOfCdt0.get(cdt0) ?? cdt0;
    (byFamily.get(family) ?? byFamily.set(family, []).get(family)!).push(cdt0);
  }

  const brandRoleAttr = ((): string | undefined => {
    let best: { attr: string; agreement: number } | undefined;
    for (const attr of columnCandidates) {
      let both = 0;
      let agree = 0;
      for (const item of items) {
        const brand = String(item.attrs[DEFAULT_COLUMN_ATTR] ?? "");
        const value = String(item.attrs[attr] ?? "");
        if (isPlaceholderValue(brand) || isPlaceholderValue(value)) continue;
        both += 1;
        if (sanitizeCdtValue(brand).toLowerCase() === sanitizeCdtValue(value).toLowerCase())
          agree += 1;
      }
      const agreement = both > 0 ? agree / both : 0;
      if (agreement >= 0.9 && (!best || agreement > best.agreement)) best = { attr, agreement };
    }
    return best?.attr;
  })();
  // Related strata share one vocabulary so restrictions stay addressable.
  for (const [family, familyCdt0s] of byFamily) {
    if (familyCdt0s.length < 2) continue;
    const votes = new Map<string, number>();
    let familyItems = 0;
    let bestScore = 0;
    for (const cdt0 of familyCdt0s) {
      const attr = cdt1AttrOf.get(cdt0);
      const size = byCdt0.get(cdt0)?.length ?? 0;
      familyItems += size;
      if (!attr) continue;
      votes.set(attr, (votes.get(attr) ?? 0) + size);

      const reading = measureBlockingOf(items, attr, runs, branchScopesOf(byCdt0.get(cdt0) ?? []));
      const stableColumns = reading.values.filter(
        (value) => value.stability >= VERTICAL_SCORE,
      ).length;
      if (stableColumns >= 2)
        bestScore = Math.max(bestScore, scoresOfCdt0.get(cdt0)?.get(attr) ?? 0);
    }
    const ranked = [...votes.entries()].sort((a, b) => b[1] - a[1]);
    let majority = ranked[0]?.[0];
    if (!majority) continue;
    const margin =
      familyItems > 0 ? ((ranked[0]?.[1] ?? 0) - (ranked[1]?.[1] ?? 0)) / familyItems : 1;
    const nearTie = ranked.length > 1 && margin < BRANCH_COLUMN_MARGIN;
    const nothingColumnar = bestScore < VERTICAL_SCORE;
    if (brandRoleAttr && majority !== brandRoleAttr && (nearTie || nothingColumnar)) {
      advisories.push(
        `"${family}": the family vote is ${nearTie ? `a near tie (${ranked.map(([attr, count]) => `"${attr}" ${count}`).join(" vs ")}, margin ${Math.round(margin * 100)}% < ${BRANCH_COLUMN_MARGIN * 100}%)` : "not close"}${nothingColumnar ? ` and no attribute tiles any stratum as >= 2 stable columns (best structural score ${bestScore.toFixed(2)} < ${VERTICAL_SCORE})` : ""} — rule 16 breaks it to the brand role ("${brandRoleAttr}"), the unit kept whole and addressable by restrictions.`,
      );
      majority = brandRoleAttr;
    }
    for (const cdt0 of familyCdt0s) {
      const chosen = cdt1AttrOf.get(cdt0);
      if (chosen === majority) continue;
      const speaks = (byCdt0.get(cdt0) ?? []).some(
        (item) => !isPlaceholderValue(item.attrs[majority]),
      );
      if (!speaks) continue;

      cdt1AttrOf.set(cdt0, majority);
      if (chosen) {
        advisories.push(
          `"${cdt0}" measured "${chosen}" as its column but conforms to "${family}"'s majority attribute "${majority}" — one segment vocabulary per family.`,
        );
      } else {
        const stale = findings.findIndex(
          (finding) => finding.check === "cdt1-empty" && finding.message.includes(`"${cdt0}"`),
        );
        if (stale >= 0) findings.splice(stale, 1);
      }
    }
  }

  const rawCdt1Of = (item: StudioItem): string => {
    const attr = cdt1AttrOf.get(cdt0Of(item)) ?? "";
    return attr === "" ? "" : sanitizeCdtValue(String(item.attrs[attr] ?? ""));
  };

  const poolOfPair = new Map<string, string>();
  const cdt1Of = (item: StudioItem): string => {
    const raw = rawCdt1Of(item);
    return poolOfPair.get(pairKeyOf(cdt0Of(item), raw)) ?? raw;
  };

  const attrValueOf = resolveAttrValue;
  const shelvesOf = (skuItems: readonly StudioItem[]): number[] =>
    [
      ...new Set(skuItems.flatMap((item) => item.placements.map((placement) => placement.shelf))),
    ].sort((a, b) => a - b);
  const rowOf = (item: StudioItem): number | undefined =>
    walk.firstPlacement.get(item.skuId)?.shelf;
  const kidItemsIn = (
    nodeItems: readonly StudioItem[],
    node: TreeNode,
    value: string,
  ): StudioItem[] => nodeItems.filter((item) => attrValueOf(item, node.attr) === value);

  const isRowPartition = (node: TreeNode, nodeItems: readonly StudioItem[]): boolean => {
    if (node.reading !== "vertical") return false;
    const blocks = node.kids
      .filter((kid) => kid.count >= 2)
      .map((kid) => new Set(kidItemsIn(nodeItems, node, kid.value).map(rowOf)));
    if (blocks.length < 2) return false;
    const seen = new Set<number | undefined>();
    for (const shelves of blocks) {
      for (const shelf of shelves) {
        if (seen.has(shelf)) return false;
        seen.add(shelf);
      }
    }
    return true;
  };

  const nodeCandidatesOf = (cdt0: string): string[] => {
    const cdt1Attr = cdt1AttrOf.get(cdt0) ?? "";
    const bandAttr = strataResult.strata.find((entry) => entry.cdt0 === cdt0)?.bandAttr;
    return candidates.filter((attr) => attr !== cdt1Attr && attr !== bandAttr);
  };
  const columnsOf = (branchItems: readonly StudioItem[]): Map<string, StudioItem[]> => {
    const columns = new Map<string, StudioItem[]>();
    for (const item of branchItems) {
      const value = cdt1Of(item);
      (columns.get(value) ?? columns.set(value, []).get(value)!).push(item);
    }
    return columns;
  };
  const rowHomeKeyOf = (rule: Pick<ReplicaRowHome, "cdt0" | "cdt1" | "attr" | "value">): string =>
    [rule.cdt0, rule.cdt1, rule.attr, rule.value].join("\u0000");

  const magnitudeOf = (value: string): number | null => {
    if (value === NONE_VALUE) return null;
    const match = value.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  };
  const cutBaseOfAttr = (attr: string): string | null => {
    const index = attr.indexOf(" >= ");
    return index < 0 ? null : attr.slice(0, index);
  };
  const rowsKeyOf = (skuItems: readonly StudioItem[]): string =>
    [...new Set(skuItems.map(rowOf))].sort((a, b) => (a ?? 0) - (b ?? 0)).join(",");
  interface Seed {
    baseAttr: string;
    numeric: boolean;

    sideRows: readonly string[];

    sideValues: readonly (readonly string[])[];
  }

  const conformTo = (seed: Seed, nodeItems: readonly StudioItem[]): StudioItem[][] | null => {
    if (seed.numeric) {
      const sideCount = seed.sideRows.length;
      // Strict (default): exactly two sides, each an exact row-set match.
      // Relaxed (`relaxedRowPartitions`): k sides; the node's own thresholds
      // must stack its groups top to bottom without interleaving, each group
      // touching its seed side's rows — a ragged edge conforms.
      if (!relaxedRowPartitions && sideCount !== 2) return null;
      if (sideCount < 2) return null;
      const withMagnitude = nodeItems.map((item) => ({
        item,
        magnitude: magnitudeOf(attrValueOf(item, seed.baseAttr)),
      }));
      if (withMagnitude.some((entry) => entry.magnitude === null)) return null;
      const distinct = [...new Set(withMagnitude.map((entry) => entry.magnitude!))].sort(
        (a, b) => a - b,
      );
      if (distinct.length < sideCount) return null;
      const sideRowSets = seed.sideRows.map((key) => new Set(key.split(",").map(Number)));
      const rowsOfGroup = (group: readonly StudioItem[]): number[] =>
        [...new Set(group.map(rowOf))]
          .filter((row): row is number => row !== undefined)
          .sort((a, b) => a - b);
      const conforms = (groups: readonly StudioItem[][]): boolean => {
        if (groups.some((group) => group.length < 2)) return false;
        if (!relaxedRowPartitions) {
          return groups.every((group, index) => rowsKeyOf(group) === seed.sideRows[index]);
        }
        const rows = groups.map(rowsOfGroup);
        if (rows.some((entry) => entry.length === 0)) return false;
        // Stack in the seed's own direction: a wall may put its large sizes on top.
        const topOf = (set: ReadonlySet<number>) => Math.min(...set);
        const smallOnTop = topOf(sideRowSets[0]) <= topOf(sideRowSets[sideRowSets.length - 1]);
        const stacked = smallOnTop ? rows : [...rows].reverse();
        for (let index = 0; index + 1 < stacked.length; index += 1) {
          if (stacked[index][stacked[index].length - 1] >= stacked[index + 1][0]) return false;
        }
        return rows.every((entry, index) => entry.some((row) => sideRowSets[index].has(row)));
      };
      // Every choice of (sideCount - 1) thresholds among the distinct sizes.
      const candidates = distinct.slice(1);
      const combos: number[][] = [];
      const choose = (start: number, picked: number[]): void => {
        if (picked.length === sideCount - 1) {
          combos.push([...picked]);
          return;
        }
        for (let index = start; index < candidates.length; index += 1) {
          picked.push(candidates[index]);
          choose(index + 1, picked);
          picked.pop();
        }
      };
      choose(0, []);
      for (const thresholds of combos) {
        const groups: StudioItem[][] = Array.from({ length: sideCount }, () => []);
        for (const entry of withMagnitude) {
          let side = 0;
          while (side < thresholds.length && entry.magnitude! >= thresholds[side]) side += 1;
          groups[side].push(entry.item);
        }
        if (conforms(groups)) return groups;
      }
      return null;
    }
    const sides: StudioItem[][] = seed.sideRows.map(() => []);
    const byValue = new Map<string, StudioItem[]>();
    for (const item of nodeItems) {
      const value = attrValueOf(item, seed.baseAttr);
      (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
    }
    for (const [value, valueItems] of byValue) {
      const side = seed.sideValues.findIndex((values) => values.includes(value));
      if (side < 0) return null;
      const rows = new Set(valueItems.map(rowOf));
      const sideRows = new Set(seed.sideRows[side].split(",").map(Number));
      if (![...rows].every((row) => row !== undefined && sideRows.has(row))) return null;
      sides[side].push(...valueItems);
    }
    if (sides.some((sideItems) => sideItems.length < 2)) return null;
    return sides;
  };

  const MAJORITY_SHARE = 0.5;
  /** keepBrandSizeRows: the share of its cdt0's width a brand-private row
   *  partition (with the items that agree with it) must hold to be carved. */
  const PRIVATE_PARTITION_SHARE = 0.8;
  /** The draft's levels read vertical (size stacked by row), below the root. */
  const verticalLevelAttrs = (draft?.spine ?? [])
    .filter((level) => level.orient === "vertical" && level.attr !== sectionAttr)
    .map((level) => level.attr);
  const widthOfItems = (skuItems: readonly StudioItem[]): number =>
    skuItems.reduce((sum, item) => sum + item.placements.reduce((s, p) => s + p.widthCm, 0), 0);
  const preRowHomes: ReplicaRowHome[] = [];
  const privateRowHomes: ReplicaRowHome[] = [];
  const privateRowHomeKeys = new Set<string>();
  const groupOfRule = new Map<ReplicaRowHome, string>();
  for (const [cdt0, branchItems] of byCdt0) {
    const nodeCandidates = nodeCandidatesOf(cdt0);
    const nodes = [...columnsOf(branchItems)];

    {
      const cdt1Attr = cdt1AttrOf.get(cdt0) ?? "";
      const rowAttrs = [...new Set([cdt1Attr, ...columnCandidates])].filter((attr) => attr !== "");
      let carvedByRow = false;
      for (const attr of rowAttrs) {
        const byValue = new Map<string, StudioItem[]>();
        for (const item of branchItems) {
          const value = attrValueOf(item, attr);
          (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
        }
        if (byValue.size < 2) continue;
        const perValue = [...byValue.entries()].map(([value, valueItems]) => {
          const byRow = new Map<number, StudioItem[]>();
          for (const item of valueItems) {
            const row = rowOf(item);
            if (row === undefined) continue;
            (byRow.get(row) ?? byRow.set(row, []).get(row)!).push(item);
          }
          return { value, valueItems, byRow };
        });

        if (
          perValue.some(
            (entry) =>
              [...entry.byRow.values()].filter((rowItems) => rowItems.length >= 2).length >= 2,
          )
        )
          continue;
        const singleRowValues = perValue.filter((entry) => entry.byRow.size === 1);
        const rowsOfSingle = new Set(singleRowValues.map((entry) => [...entry.byRow.keys()][0]));
        const singleShare =
          widthOfItems(branchItems) > 0
            ? widthOfItems(singleRowValues.flatMap((entry) => entry.valueItems)) /
              widthOfItems(branchItems)
            : 0;
        if (rowsOfSingle.size < 2) continue;
        if (singleShare < MAJORITY_SHARE) {
          advisories.push(
            `"${cdt0}": ${attr} values sit on separate rows (${[...rowsOfSingle].sort((a, b) => a - b).join(", ")}) but the single-row values hold ${Math.round(singleShare * 100)}% of the width (majority floor ${MAJORITY_SHARE * 100}%) — not carved by row on ${attr}.`,
          );
          continue;
        }

        const byCdt1Row = new Map<string, { cdt1: string; row: number; items: StudioItem[] }>();
        for (const item of branchItems) {
          const row = rowOf(item);
          if (row === undefined) continue;
          const cdt1 = cdt1Of(item);
          const key = `${cdt1}\u0000${row}`;
          (byCdt1Row.get(key) ?? byCdt1Row.set(key, { cdt1, row, items: [] }).get(key)!).items.push(
            item,
          );
        }
        for (const { cdt1, row, items: rowItems } of byCdt1Row.values()) {
          const rule: ReplicaRowHome = {
            cdt0,
            cdt1,
            attr: `${attr} (rows)`,
            value: `row ${row}`,
            shelves: [row],
            skuIds: rowItems.map((item) => item.skuId),
            shipped: carveByRowAttribute,
          };
          if (carveByRowAttribute) {
            preRowHomes.push(rule);
            groupOfRule.set(rule, `${cdt0} / rows`);
          } else if (!privateRowHomeKeys.has(rowHomeKeyOf(rule))) {
            privateRowHomeKeys.add(rowHomeKeyOf(rule));
            privateRowHomes.push(rule);
          }
        }
        const spread = perValue.filter((entry) => entry.byRow.size > 1);
        const fact = `"${cdt0}" is partitioned by row on ${attr}${attr === cdt1Attr ? " (its cdt1)" : ` (cdt1 is ${cdt1Attr})`}: ${singleRowValues.map((entry) => `"${entry.value}" row ${[...entry.byRow.keys()][0]}`).join(", ")} (${Math.round(singleShare * 100)}% of the width)${spread.length > 0 ? `; ${spread.map((entry) => `"${entry.value}"`).join(", ")} spread over rows` : ""}`;
        advisories.push(
          carveByRowAttribute
            ? `${fact} — carved into one cdt0 sub-stratum per row (§9: a vertical fact above cdt1 goes to cdt0).`
            : `${fact} — NOT carved (a cdt0 is never carved by its own cdt1; slotting variants by row name would stop products moving between shelves): the band ships whole with its brand columns inside, the fact is recorded in rowHomes (shipped: false).`,
        );
        carvedByRow = carveByRowAttribute;
        break;
      }
      if (carvedByRow) continue;
    }

    /* 2b-stack (opt-in `carveStackedBrands`): a brand standing on a PROPER
       SUBSET of its section's rows in every bay it occupies is stacked over or
       under other brands there — the tail bay's slabs, a store brand under a
       national brand. A cdt1 column is full height in vertical mode and cdt2
       cannot pin a shelf, so the only carrier the vocabulary has is a cdt0
       sub-stratum named for the brand, fenced to its rows. Items of the brand
       that stray off those rows stay in the parent. */
    if (carveStackedBrands) {
      const STACK_ROW_SHARE = 0.1;
      const STACK_PURITY = 0.9;
      const SECTION_ROW_SHARE = 0.03;
      /* A slab is worth a variant of its own when it is a block, not a tuck-in:
         five SKUs, or a whole bay of shelf, and a section sheds at most two of
         them. Every slab is one more rectangle in the mudmap solve and one less
         SKU for the parent it leaves behind; see `stackedBrandFloor`. */
      const STACK_MIN_SKUS = stackedBrandFloor.minSkus;
      const STACK_MIN_WIDTH_SHARE = stackedBrandFloor.minBayShare;
      const STACK_MAX_PER_SECTION = stackedBrandFloor.maxPerSection;
      const bayWidthCm = runs[0]?.bayWidthCm ?? 0;
      /** A block, not a tuck-in: the same floor the partial-cake bands clear. */
      const clearsFloor = (skuCount: number, widthCm: number): boolean =>
        clearsStackFloor(
          { minSkus: STACK_MIN_SKUS, minBayShare: STACK_MIN_WIDTH_SHARE },
          skuCount,
          widthCm,
          bayWidthCm,
        );
      const floorText = `${STACK_MIN_SKUS} SKUs or ${
        STACK_MIN_WIDTH_SHARE === 1 ? "a whole bay" : `${STACK_MIN_WIDTH_SHARE} of a bay`
      }`;
      const cdt1Attr = cdt1AttrOf.get(cdt0) ?? "";
      const runOf = new Map(runs.map((run) => [run.run, run]));
      const bayOfPlacement = (placement: StudioItem["placements"][number]): number =>
        placementBayOf(placement, runOf.get(placement.run));
      const shelfWidthsOf = (
        skuItems: readonly StudioItem[],
        baySet?: ReadonlySet<number>,
      ): Map<number, number> => {
        const widths = new Map<number, number>();
        for (const item of skuItems) {
          for (const placement of item.placements) {
            if (baySet && !baySet.has(bayOfPlacement(placement))) continue;
            widths.set(placement.shelf, (widths.get(placement.shelf) ?? 0) + placement.widthCm);
          }
        }
        return widths;
      };
      const contiguousRanks = (ranks: readonly number[]): boolean =>
        ranks.every((rank, index) => index === 0 || rank === ranks[index - 1] + 1);
      const sectionShelves = [
        ...new Set(branchItems.flatMap((item) => item.placements.map((p) => p.shelf))),
      ].sort((a, b) => a - b);
      /** A brand that passed the shape and the floor: a slab this section could
       *  shed. The cap and the residual test choose among them below. */
      interface SlabCandidate {
        brand: string;
        rows: number[];
        sectionRows: number[];
        bays: number[];
        skuIds: string[];
        widthCm: number;
      }
      const slabs: SlabCandidate[] = [];
      if (cdt1Attr !== "") {
        for (const [brand, brandItems] of columnsOf(branchItems)) {
          if (brandItems.length < 2) continue;
          const widths = shelfWidthsOf(brandItems);
          const total = [...widths.values()].reduce((sum, width) => sum + width, 0);
          if (!(total > 0)) continue;
          const rows = sectionShelves.filter(
            (shelf) => (widths.get(shelf) ?? 0) / total >= STACK_ROW_SHARE,
          );
          const purity = rows.reduce((sum, shelf) => sum + (widths.get(shelf) ?? 0), 0) / total;
          if (rows.length === 0 || purity < STACK_PURITY || !contiguousRanks(rows)) continue;
          const brandBays = new Set(
            brandItems.flatMap((item) => item.placements.map(bayOfPlacement)),
          );
          const sectionWidths = shelfWidthsOf(branchItems, brandBays);
          const sectionTotal = [...sectionWidths.values()].reduce((sum, width) => sum + width, 0);
          const sectionRows = sectionShelves.filter(
            (shelf) => (sectionWidths.get(shelf) ?? 0) / sectionTotal >= SECTION_ROW_SHARE,
          );
          // Spanning the section's rows in its bays is a column, not a slab.
          if (rows.length >= sectionRows.length) continue;
          const skuIds = brandItems
            .filter((item) => {
              const row = rowOf(item);
              return row !== undefined && rows.includes(row);
            })
            .map((item) => item.skuId);
          if (skuIds.length < 2) continue;
          if (!clearsFloor(skuIds.length, total)) {
            advisories.push(
              `"${cdt0} › ${brand}" stands on rows ${rows.join(",")} only, but with ${skuIds.length} SKU(s) over ${lengthLabel(total, lengthUnit)} it is a tuck-in, not a slab; it rides "${cdt0}" (carveStackedBrands floor: ${floorText}).`,
            );
            continue;
          }
          slabs.push({
            brand,
            rows,
            sectionRows,
            bays: [...brandBays].sort((a, b) => a - b),
            skuIds,
            widthCm: total,
          });
        }
      }
      /* Which of them the section actually sheds. Two gates, both about the
         cost a slab puts on the mudmap solve rather than on the fact itself:
         at most `maxPerSection` slabs, the widest first, and never a residual
         parent too small to earn a cell of its own. */
      const widest = [...slabs].sort(
        (a, b) =>
          b.widthCm - a.widthCm ||
          b.skuIds.length - a.skuIds.length ||
          a.brand.localeCompare(b.brand),
      );
      const kept = new Set(widest.slice(0, STACK_MAX_PER_SECTION));
      for (const slab of widest.slice(STACK_MAX_PER_SECTION)) {
        advisories.push(
          `"${cdt0} › ${slab.brand}" is a slab on rows ${slab.rows.join(",")}, but ${
            kept.size > 0
              ? `"${cdt0}" already sheds the wider ${[...kept]
                  .map((entry) => `"${entry.brand}"`)
                  .join(", ")}`
              : `"${cdt0}" sheds no slab at all`
          }; with ${slab.skuIds.length} SKU(s) over ${lengthLabel(slab.widthCm, lengthUnit)} it rides "${cdt0}" (carveStackedBrands cap: ${STACK_MAX_PER_SECTION} slab(s) per section).`,
        );
      }
      /* The residual test. A parent every brand leaves is fine — downstream it
         becomes a virtual band — but a parent left with a handful of SKUs is a
         sliver: it earns no mudmap cell and the solver culls it. Fold the
         smallest slab back until what stays clears the same floor. */
      while (kept.size > 0) {
        const carved = new Set([...kept].flatMap((slab) => slab.skuIds));
        const residual = branchItems.filter((item) => !carved.has(item.skuId));
        if (residual.length === 0) break;
        const residualWidth = widthOfItems(residual);
        if (clearsFloor(residual.length, residualWidth)) break;
        const smallest = [...kept].sort(
          (a, b) =>
            a.widthCm - b.widthCm ||
            a.skuIds.length - b.skuIds.length ||
            a.brand.localeCompare(b.brand),
        )[0];
        kept.delete(smallest);
        advisories.push(
          `"${cdt0} › ${smallest.brand}" is a slab on rows ${smallest.rows.join(",")}, but carving it leaves "${cdt0}" itself ${residual.length} SKU(s) over ${lengthLabel(residualWidth, lengthUnit)} — a residual under the slab floor (${floorText}) earns no cell of its own and is culled, so the slab is folded back into "${cdt0}" (carveStackedBrands).`,
        );
      }
      for (const slab of slabs) {
        if (!kept.has(slab)) continue;
        const rule: ReplicaRowHome = {
          cdt0,
          cdt1: slab.brand,
          attr: cdt1Attr,
          value: slab.brand,
          shelves: slab.rows,
          skuIds: slab.skuIds,
          shipped: true,
          label: slab.brand,
        };
        preRowHomes.push(rule);
        groupOfRule.set(rule, `${cdt0} / stacked / ${slab.brand}`);
        advisories.push(
          `"${cdt0} › ${slab.brand}" stands on rows ${slab.rows.join(",")} of the section's rows ${slab.sectionRows.join(",")} in bay(s) ${slab.bays.join(",")}: a brand stacked over or under other brands, which a full-height cdt1 column cannot say — carved into the cdt0 sub-stratum "${cdt0} ${slab.brand}" fenced to those rows (carveStackedBrands).`,
        );
      }
    }

    interface RootPartition {
      cdt1: string;
      node: TreeNode;
      items: readonly StudioItem[];
      seedKey: string;
    }
    const seeds = new Map<string, Seed>();
    const rootPartitions: RootPartition[] = [];
    /** Brand-private candidates read on a vertical level of the draft rather
     *  than on the node's tree (keepBrandSizeRows). */
    const levelPartitions: {
      cdt1: string;
      items: readonly StudioItem[];
      seedKey: string;
      seed: Seed;
    }[] = [];
    /** Relaxed seeds: the node's values of `attr`, grouped by the ROW SET they
     *  stand on. Values sharing rows (4- and 6-roll packs both on the top
     *  shelf) are one side; a partition needs >= 2 sides with pairwise
     *  disjoint rows, each of >= 2 items, holding >= 90% of the node. This
     *  reads a three-tier ladder that the tree can only cut in two. */
    interface RowGroup {
      values: string[];
      items: StudioItem[];
      rowsKey: string;
    }
    const rowGroupsByAttr = (attr: string, nodeItems: readonly StudioItem[]): RowGroup[] | null => {
      const byValue = new Map<string, StudioItem[]>();
      for (const item of nodeItems) {
        const value = attrValueOf(item, attr);
        if (value === NONE_VALUE) continue;
        (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
      }
      if (byValue.size < 2) return null;
      const byRows = new Map<string, RowGroup>();
      for (const [value, valueItems] of byValue) {
        const rowsKey = rowsKeyOf(valueItems);
        const group =
          byRows.get(rowsKey) ??
          byRows.set(rowsKey, { values: [], items: [], rowsKey }).get(rowsKey)!;
        group.values.push(value);
        group.items.push(...valueItems);
      }
      const groups = [...byRows.values()].filter((group) => group.items.length >= 2);
      if (groups.length < 2) return null;
      const seenRows = new Set<number>();
      for (const group of groups) {
        for (const row of group.rowsKey.split(",").map(Number)) {
          if (seenRows.has(row)) return null;
          seenRows.add(row);
        }
      }
      const covered = groups.reduce((sum, group) => sum + group.items.length, 0);
      if (covered < 0.9 * nodeItems.length) return null;
      return groups;
    };
    /** A partition's sides as a seed: numeric sides small to large, the
     *  others in the tree's order or (`byRow`) top row first. */
    const seedOfGroups = (
      groups: readonly RowGroup[],
      baseAttr: string,
      cut: boolean,
      byRow: boolean,
    ): { seed: Seed; seedKey: string } => {
      const numeric =
        cut || groups.every((group) => group.values.every((value) => magnitudeOf(value) !== null));
      const sides = numeric
        ? [...groups].sort(
            (a, b) =>
              Math.min(...a.items.map((item) => magnitudeOf(attrValueOf(item, baseAttr)) ?? 0)) -
              Math.min(...b.items.map((item) => magnitudeOf(attrValueOf(item, baseAttr)) ?? 0)),
          )
        : byRow
          ? [...groups].sort(
              (a, b) => Number(a.rowsKey.split(",")[0]) - Number(b.rowsKey.split(",")[0]),
            )
          : groups;
      const seed: Seed = {
        baseAttr,
        numeric,
        sideRows: sides.map((side) => side.rowsKey),
        sideValues: sides.map((side) => side.values),
      };
      const seedKey = [
        baseAttr,
        numeric ? "numeric" : seed.sideValues.map((values) => values.join("/")).join("|"),
        seed.sideRows.join("|"),
      ].join("\u0000");
      return { seed, seedKey };
    };
    for (const [cdt1, columnItems] of nodes) {
      const tree = inferTree(columnItems, runs, nodeCandidates, attrValueOf);
      if (!tree) continue;
      let baseAttr = cutBaseOfAttr(tree.attr) ?? tree.attr;
      let groups: RowGroup[] | null = null;
      if (relaxedRowPartitions) {
        /* The root split first, then every other candidate: a brand whose
           tree opens on scent still stacks its sizes by row. The first
           attribute that partitions the node's rows wins. */
        for (const attr of [baseAttr, ...nodeCandidates.filter((a) => a !== baseAttr)]) {
          const found = rowGroupsByAttr(attr, columnItems);
          if (found) {
            groups = found;
            baseAttr = attr;
            break;
          }
        }
      }
      /* keepBrandSizeRows: a node whose tree opens on something else (benefit
         tier above size) can still stack a vertical level of the draft by row.
         Read that way, it seeds only the brand-private pass below: the shared
         pass keeps conforming on the tree's own root split. */
      let privateOnly = false;
      if (!groups && keepBrandSizeRows && !isRowPartition(tree, columnItems)) {
        for (const attr of verticalLevelAttrs.filter((a) => nodeCandidates.includes(a))) {
          const found = rowGroupsByAttr(attr, columnItems);
          if (found) {
            groups = found;
            baseAttr = attr;
            privateOnly = true;
            break;
          }
        }
      }
      if (!groups) {
        if (!isRowPartition(tree, columnItems)) continue;
        groups = tree.kids
          .filter((kid) => kid.count >= 2)
          .map((kid) => {
            const kidItems = kidItemsIn(columnItems, tree, kid.value);
            return { values: [kid.value], items: kidItems, rowsKey: rowsKeyOf(kidItems) };
          });
      }
      // The tree's cut says nothing about an attribute the relaxed search switched to.
      const { seed, seedKey } = seedOfGroups(
        groups,
        baseAttr,
        cutBaseOfAttr(tree.attr) === baseAttr,
        relaxedRowPartitions || privateOnly,
      );
      if (privateOnly) {
        levelPartitions.push({ cdt1, items: columnItems, seedKey, seed });
        continue;
      }
      seeds.set(seedKey, seed);
      rootPartitions.push({ cdt1, node: tree, items: columnItems, seedKey });
    }
    const sharedSeeds = new Set<string>();
    /** One cdt0 sub-stratum per row side of `seed`: the conforming nodes by
     *  their own sides, every other node of the stratum by the row its items
     *  stand on. */
    const carveSeed = (
      seedKey: string,
      seed: Seed,
      conforming: readonly { cdt1: string; sides: StudioItem[][] }[],
    ): { joinedByRow: string[]; leftBehind: number } => {
      sharedSeeds.add(seedKey);
      const numericTierNames =
        seed.sideRows.length === 2
          ? ["small", "large"]
          : seed.sideRows.length === 3
            ? ["small", "medium", "large"]
            : seed.sideRows.map((_rows, index) => `tier ${index + 1}`);
      const sideLabel = (index: number): string =>
        seed.numeric ? numericTierNames[index] : seed.sideValues[index].join("/");
      // A cut-derived band keeps its base attribute available below (the
      // ladder still orders within the row): the band attribute is named
      // "<attr> (cut)", which `nodeCandidatesOf` does not match.
      const attr = seed.numeric ? `${seed.baseAttr} (cut)` : seed.baseAttr;

      const sideRowSets = seed.sideRows.map((key) => new Set(key.split(",").map(Number)));
      /* Relaxed: a side's rows are the UNION over the conforming brands, so
         one sub-stratum per side collects every brand (the rules must share
         one shelf key to group), and the per-bay fence keeps each brand's own
         edge. Strict: the seed's exact rows. */
      if (relaxedRowPartitions) {
        /** side -> row -> width its conforming items stand there. */
        const widthOn = sideRowSets.map(() => new Map<number, number>());
        for (const entry of conforming) {
          entry.sides.forEach((sideItems, index) => {
            for (const item of sideItems) {
              const row = rowOf(item);
              if (row === undefined) continue;
              sideRowSets[index].add(row);
              widthOn[index].set(row, (widthOn[index].get(row) ?? 0) + widthOfItems([item]));
            }
          });
        }
        /* Brands turning at different rows make the unions overlap, and two
           sub-strata homed on one row clash in the materializer's cake. The
           row goes to the side most of its width stands in (the seed's side
           on a tie); the per-bay fence still opens each brand's own shelves. */
        const seedSideOf = (row: number) =>
          seed.sideRows.findIndex((key) => key.split(",").map(Number).includes(row));
        for (const row of new Set(sideRowSets.flatMap((rows) => [...rows]))) {
          const holders = sideRowSets
            .map((rows, index) => (rows.has(row) ? index : -1))
            .filter((index) => index >= 0);
          if (holders.length < 2) continue;
          const keep = [...holders].sort(
            (a, b) =>
              (widthOn[b].get(row) ?? 0) - (widthOn[a].get(row) ?? 0) ||
              Number(b === seedSideOf(row)) - Number(a === seedSideOf(row)) ||
              a - b,
          )[0];
          for (const index of holders) {
            // A side keeps at least one row of its own.
            if (index !== keep && sideRowSets[index].size > 1) sideRowSets[index].delete(row);
          }
        }
      }
      const sideShelves = sideRowSets.map((rows) => [...rows].sort((a, b) => a - b));
      const joinedByRow: string[] = [];
      let leftBehind = 0;
      for (const [cdt1, nodeItems] of nodes) {
        let sides = conforming.find((entry) => entry.cdt1 === cdt1)?.sides ?? null;
        if (!sides) {
          sides = sideRowSets.map(() => []);
          for (const item of nodeItems) {
            const row = rowOf(item);
            const side = sideRowSets.findIndex((rows) => row !== undefined && rows.has(row));
            if (side < 0) leftBehind += 1;
            else sides[side].push(item);
          }
          joinedByRow.push(cdt1);
        }
        sides.forEach((sideItems, index) => {
          if (sideItems.length === 0) return;
          const rule: ReplicaRowHome = {
            cdt0,
            cdt1,
            attr,
            value: sideLabel(index),
            shelves: sideShelves[index],
            skuIds: sideItems.map((item) => item.skuId),
            shipped: true,
          };
          preRowHomes.push(rule);
          groupOfRule.set(rule, `${cdt0} / ${seedKey}`);
        });
      }
      return { joinedByRow, leftBehind };
    };
    for (const [seedKey, seed] of seeds) {
      const conforming = nodes
        .map(([cdt1, nodeItems]) => ({ cdt1, sides: conformTo(seed, nodeItems) }))
        .filter((entry): entry is { cdt1: string; sides: StudioItem[][] } => entry.sides !== null);
      if (conforming.length < 2) continue;

      const cdt0Width = widthOfItems(branchItems);
      const conformingWidth = conforming.reduce(
        (sum, entry) => sum + widthOfItems(nodes.find(([cdt1]) => cdt1 === entry.cdt1)?.[1] ?? []),
        0,
      );
      const conformingShare = cdt0Width > 0 ? conformingWidth / cdt0Width : 0;
      // A minority pattern stays node-local instead of reshaping the whole stratum.
      if (conformingShare < MAJORITY_SHARE) {
        advisories.push(
          `"${cdt0}": ${conforming.map((entry) => `"${entry.cdt1}"`).join(", ")} share a row partition on ${seed.baseAttr}, but they hold ${Math.round(conformingShare * 100)}% of the stratum's width (majority floor ${MAJORITY_SHARE * 100}%) — a minority rule, not carved; each stays in its node, folded into cdt3 (lossy).`,
        );
        continue;
      }
      const { joinedByRow, leftBehind } = carveSeed(seedKey, seed, conforming);
      advisories.push(
        `"${cdt0}" shares a row partition on ${seed.baseAttr}${seed.numeric ? " (each brand at its own threshold)" : ""}: ${conforming.map((entry) => `"${entry.cdt1}"`).join(", ")} follow it (${Math.round(conformingShare * 100)}% of the width)${joinedByRow.length > 0 ? `; ${joinedByRow.map((cdt1) => `"${cdt1}"`).join(", ")} join by the row their items sit on` : ""}${leftBehind > 0 ? `; ${leftBehind} SKU(s) on rows outside the partition stay in "${cdt0}"` : ""} — concatenated upward into one cdt0 sub-stratum per row side (§9: shared by the zone's brands).`,
      );
    }
    /* keepBrandSizeRows: a row partition no other node of the stratum
       follows is carved all the same when it is clean (§9 sends a vertical
       fact upward only when it is shared, and every store then flipped a
       brand's small and large packs). Clean: it reads vertical, every value of
       it stands on its own side's rows, and with the other nodes' items that
       agree with it, it holds most of the brand block. The other nodes join by
       row, as they do a shared partition. An unclean one stays folded into
       cdt3 (shipped: false). */
    const cleanPartitionOf = (
      seed: Seed,
      /** null: `nodeItems` is the whole block, so no other node joins. */
      cdt1: string | null,
      nodeItems: readonly StudioItem[],
    ): { sides: StudioItem[][]; agreeingWidth: number } | null => {
      const sides = conformTo(seed, nodeItems);
      if (!sides) return null;
      const sideRowSets = seed.sideRows.map((key) => new Set(key.split(",").map(Number)));
      const standsOn = (item: StudioItem, side: number): boolean =>
        item.placements.length > 0 &&
        item.placements.every((placement) => sideRowSets[side].has(placement.shelf));
      const sideOfValue = new Map<string, number>();
      for (const [side, sideItems] of sides.entries()) {
        for (const item of sideItems) {
          if (!standsOn(item, side)) return null;
          const value = attrValueOf(item, seed.baseAttr);
          // One value on two sides' rows is a spread, not a partition.
          if ((sideOfValue.get(value) ?? side) !== side) return null;
          sideOfValue.set(value, side);
        }
      }
      const magnitudes = sides.map((sideItems) =>
        sideItems.map((item) => magnitudeOf(attrValueOf(item, seed.baseAttr)) ?? 0),
      );
      const agrees = (item: StudioItem, side: number): boolean => {
        const value = attrValueOf(item, seed.baseAttr);
        if (!seed.numeric) return seed.sideValues[side].includes(value);
        const magnitude = magnitudeOf(value);
        if (magnitude === null) return false;
        return (
          (side === 0 || magnitude > Math.max(...magnitudes[side - 1])) &&
          (side === sides.length - 1 || magnitude < Math.min(...magnitudes[side + 1]))
        );
      };
      let agreeingWidth = widthOfItems(nodeItems);
      for (const [other, otherItems] of cdt1 === null ? [] : nodes) {
        if (other === cdt1) continue;
        for (const item of otherItems) {
          const row = rowOf(item);
          const side = sideRowSets.findIndex((rows) => row !== undefined && rows.has(row));
          if (side >= 0 && standsOn(item, side) && agrees(item, side)) {
            agreeingWidth += widthOfItems([item]);
          }
        }
      }
      return { sides, agreeingWidth };
    };
    /* A measured draft replays its bands as adopted; carving again would
       split a band the release already names. A horizontally blocked wall
       reads its rows as its own blocks already. */
    if (
      keepBrandSizeRows &&
      !draft?.measuredBands &&
      !isHorizontalWall(draft) &&
      sharedSeeds.size === 0 &&
      !preRowHomes.some((rule) => rule.cdt0 === cdt0)
    ) {
      const cdt0Width = widthOfItems(branchItems);
      interface PrivateCandidate {
        /** null: the brand block read as a whole, every node joining by row. */
        cdt1: string | null;
        items: readonly StudioItem[];
        seedKey: string;
        seed: Seed;
      }
      const pick = (candidates: readonly PrivateCandidate[]) => {
        let best: { candidate: PrivateCandidate; sides: StudioItem[][]; share: number } | null =
          null;
        for (const candidate of candidates) {
          const read = cleanPartitionOf(candidate.seed, candidate.cdt1, candidate.items);
          if (!read) continue;
          const share = cdt0Width > 0 ? read.agreeingWidth / cdt0Width : 0;
          if (share < PRIVATE_PARTITION_SHARE) continue;
          if (
            !best ||
            share > best.share ||
            (share === best.share &&
              widthOfItems(candidate.items) > widthOfItems(best.candidate.items))
          ) {
            best = { candidate, sides: read.sides, share };
          }
        }
        return best;
      };
      /* A node's own partition first (its tree's, then a vertical level of the
         draft); the whole block only where no node holds two items a side
         (Viva's double and triple rolls, one large pack each). */
      const best =
        pick([
          ...rootPartitions.map((partition) => ({
            cdt1: partition.cdt1,
            items: partition.items,
            seedKey: partition.seedKey,
            seed: seeds.get(partition.seedKey)!,
          })),
          ...levelPartitions,
        ]) ??
        pick(
          verticalLevelAttrs
            .filter((attr) => nodeCandidates.includes(attr))
            .flatMap((attr) => {
              const groups = rowGroupsByAttr(attr, branchItems);
              return groups
                ? [{ cdt1: null, items: branchItems, ...seedOfGroups(groups, attr, false, true) }]
                : [];
            }),
        );
      if (best) {
        const { cdt1, seed, seedKey } = best.candidate;
        const { joinedByRow, leftBehind } = carveSeed(
          seedKey,
          seed,
          cdt1 === null ? [] : [{ cdt1, sides: best.sides }],
        );
        const sidesText = best.sides
          .map(
            (sideItems, index) =>
              `${[...new Set(sideItems.map((item) => attrValueOf(item, seed.baseAttr)))].join("/")} on row(s) ${seed.sideRows[index]}`,
          )
          .join("; ");
        const whose =
          cdt1 === null
            ? `"${cdt0}" stacks ${seed.baseAttr} by row as one brand block (${sidesText}), no node of it holding two items a side,`
            : `"${cdt0} › ${cdt1}" opens on a brand-private row partition on ${seed.baseAttr} (${sidesText}) that no other node of "${cdt0}" follows,`;
        const joined =
          cdt1 === null
            ? "; every node joins by the row its items sit on"
            : joinedByRow.length > 0
              ? `; ${joinedByRow.map((other) => `"${other}"`).join(", ")} join by the row their items sit on`
              : "";
        advisories.push(
          `${whose} and it is clean: it reads vertical, every value stands on its own rows, and with the items that agree with it it holds ${Math.round(best.share * 100)}% of the brand block's width (floor ${PRIVATE_PARTITION_SHARE * 100}%)${joined}${leftBehind > 0 ? `; ${leftBehind} SKU(s) on rows outside the partition stay in "${cdt0}"` : ""} — carved like a shared partition, one cdt0 sub-stratum per row side (keepBrandSizeRows: each brand keeps its size rows as the base shows them).`,
        );
      }
    }
    for (const partition of rootPartitions) {
      if (sharedSeeds.has(partition.seedKey)) continue;
      for (const kid of partition.node.kids) {
        const kidItems = kidItemsIn(partition.items, partition.node, kid.value);
        const rule: ReplicaRowHome = {
          cdt0,
          cdt1: partition.cdt1,
          attr: partition.node.attr,
          value: kid.value,
          shelves: shelvesOf(kidItems),
          skuIds: kidItems.map((item) => item.skuId),
          shipped: false,
        };
        privateRowHomes.push(rule);
        privateRowHomeKeys.add(rowHomeKeyOf(rule));
      }
    }
  }
  const preHomed = withRowHomeStrata(strataResult, preRowHomes);
  for (const warning of preHomed.rowHomeWarnings) advisories.push(warning);

  const parentOfSubStratum = new Map<string, string>();
  const nodeOfSubStratum = new Map<string, string>();
  for (const stratum of preHomed.rowHomeStrata) {
    const rule = preRowHomes.find((entry) => entry.skuIds.includes(stratum.skuIds[0] ?? ""));
    if (rule) {
      parentOfSubStratum.set(stratum.cdt0, rule.cdt0);
      nodeOfSubStratum.set(stratum.cdt0, groupOfRule.get(rule) ?? `${rule.cdt0} / ${rule.cdt1}`);
    }
  }

  const emptied = preHomed.strata
    .filter((stratum) => stratum.skuIds.length === 0)
    .map((stratum) => stratum.cdt0);
  for (const cdt0 of emptied) {
    advisories.push(
      `"${cdt0}" ships no item of its own once its row partition is carved; no rows are emitted for it.`,
    );
  }
  strataResult = {
    ...preHomed,
    strata: preHomed.strata.filter((stratum) => stratum.skuIds.length > 0),
  };
  byCdt0.clear();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    (byCdt0.get(cdt0) ?? byCdt0.set(cdt0, []).get(cdt0)!).push(item);
  }
  for (const [subCdt0, parentCdt0] of parentOfSubStratum) {
    cdt1AttrOf.set(subCdt0, cdt1AttrOf.get(parentCdt0) ?? "");
  }

  const firstRankOfCdt0 = new Map<string, number>();
  for (const item of items) {
    const cdt0 = cdt0Of(item);
    const rank = walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER;
    if (rank < (firstRankOfCdt0.get(cdt0) ?? Number.MAX_SAFE_INTEGER))
      firstRankOfCdt0.set(cdt0, rank);
  }
  const firstRankOfNode = new Map<string, number>();
  for (const [subCdt0, node] of nodeOfSubStratum) {
    const rank = firstRankOfCdt0.get(subCdt0) ?? Number.MAX_SAFE_INTEGER;
    if (rank < (firstRankOfNode.get(node) ?? Number.MAX_SAFE_INTEGER))
      firstRankOfNode.set(node, rank);
  }
  const priorityRankOf = (cdt0: string): readonly number[] => {
    const own = firstRankOfCdt0.get(cdt0) ?? Number.MAX_SAFE_INTEGER;
    const node = nodeOfSubStratum.get(cdt0);
    return [node ? (firstRankOfNode.get(node) ?? own) : own, own];
  };
  const requestedOrder = [...new Set(options.cdt0Order ?? [])];
  const requestedCdt0s = new Set(requestedOrder);
  const measuredCdt0s = new Set(strataResult.cdt0Order);
  const rootOrder = [
    ...requestedOrder.filter((cdt0) => measuredCdt0s.has(cdt0)),
    ...strataResult.cdt0Order.filter((cdt0) => !requestedCdt0s.has(cdt0)),
  ];
  const clusters: string[][] = [];
  for (const cdt0 of rootOrder) {
    const parent = parentOfSubStratum.get(cdt0);
    const cluster = parent ? clusters.find((entry) => entry[0] === parent) : undefined;
    if (cluster) cluster.push(cdt0);
    else clusters.push([cdt0]);
  }
  /* The clusters themselves stand in bay-major wall order too, by their
     earliest member. In vertical blocking mode cdt0Order is a HARD x-sequence
     (a later cdt0 starts at or after an earlier one), so a stratum whose first
     item stands in bay 6 listed ahead of one anchored in bay 5 makes the
     mudmap infeasible (run_ab117_opts, 2026-09-22). The strata order it
     replaces kept a section's strata together, which the sequencing cannot
     honour once slabs start mid-zone. */
  const clusterRank = (cluster: readonly string[]): readonly number[] =>
    cluster.map(priorityRankOf).sort((a, b) => a[0] - b[0] || a[1] - b[1])[0] ?? [
      Number.MAX_SAFE_INTEGER,
      Number.MAX_SAFE_INTEGER,
    ];
  const withinCluster = (cluster: readonly string[]) =>
    [...cluster].sort((a, b) => {
      const [nodeA, ownA] = priorityRankOf(a);
      const [nodeB, ownB] = priorityRankOf(b);
      return nodeA - nodeB || ownA - ownB;
    });
  const priorityCdt0Order = options.cdt0Order?.length
    ? rootOrder
    : !startOrdered
      ? clusters.flatMap(withinCluster)
      : [...clusters]
          .sort((a, b) => {
            const [nodeA, ownA] = clusterRank(a);
            const [nodeB, ownB] = clusterRank(b);
            return nodeA - nodeB || ownA - ownB;
          })
          .flatMap(withinCluster);

  const rankIn = (skuIds: readonly string[]): number =>
    Math.min(...skuIds.map((skuId) => walk.rankOf.get(skuId) ?? Number.MAX_SAFE_INTEGER));

  // Sub-floor columns pool so Pogger can allocate them a viable width.
  const SUB_FLOOR_SHARE = 0.4;
  const POOL_CAP = 6;
  const TOP_SALES_EXEMPT = 2;
  const pools: Record<string, ReplicaPool> = {};
  const poolViability: ReplicaColumnViability[] = [];
  {
    const widthOfColumn = (colItems: readonly StudioItem[]): number =>
      colItems.reduce((sum, item) => sum + item.placements.reduce((s, p) => s + p.widthCm, 0), 0);
    const salesOfColumn = (colItems: readonly StudioItem[]): number =>
      colItems.reduce((sum, item) => sum + (item.salesAmount ?? 0), 0);
    interface Viability {
      cdt0: string;
      cdt1: string;
      subFloor: boolean;
      exempt: boolean;
      widthShare: number;
      salesShare: number;
      widthCm: number;
      items: StudioItem[];
    }
    const pairViability = new Map<string, Viability>();
    const dominantOf = new Map<string, string>();
    for (const [cdt0, branchItems] of byCdt0) {
      const cols = new Map<string, StudioItem[]>();
      for (const item of branchItems) {
        const value = cdt1Of(item);
        (cols.get(value) ?? cols.set(value, []).get(value)!).push(item);
      }
      const widths = [...cols.entries()].map(([cdt1, colItems]) => ({
        cdt1,
        colItems,
        width: widthOfColumn(colItems),
        sales: salesOfColumn(colItems),
      }));
      const meanWidth = widths.length
        ? widths.reduce((sum, entry) => sum + entry.width, 0) / widths.length
        : 0;

      const anySales = widths.some((entry) => entry.sales > 0);
      const bySales = [...widths].sort((a, b) =>
        anySales ? b.sales - a.sales : b.width - a.width,
      );
      const exempt = new Set(bySales.slice(0, TOP_SALES_EXEMPT).map((entry) => entry.cdt1));
      if (bySales.length > 0) dominantOf.set(cdt0, bySales[0].cdt1);
      const totalSales = widths.reduce((sum, entry) => sum + entry.sales, 0);
      for (const { cdt1, colItems, width, sales } of widths) {
        const oneSkuColumn = colItems.length === 1;
        pairViability.set(pairKeyOf(cdt0, cdt1), {
          cdt0,
          cdt1,
          subFloor: (meanWidth > 0 && width < meanWidth * SUB_FLOOR_SHARE) || oneSkuColumn,
          exempt: exempt.has(cdt1),
          widthShare: meanWidth > 0 ? width / meanWidth : 1,
          salesShare: totalSales > 0 ? sales / totalSales : 0,
          widthCm: width,
          items: colItems,
        });
      }
    }

    const poolable = (viability: Viability): boolean => viability.subFloor && !viability.exempt;
    for (const viability of pairViability.values()) {
      poolViability.push({
        cdt0: viability.cdt0,
        cdt1: viability.cdt1,
        items: viability.items.length,
        widthCm: Math.round(viability.widthCm * 10) / 10,
        widthShare: Math.round(viability.widthShare * 100) / 100,
        salesShare: Math.round(viability.salesShare * 1000) / 1000,
        subFloor: viability.subFloor,
        exempt: viability.exempt,
        poolable: poolable(viability),
      });
    }
    const initialsOf = (name: string): string =>
      name
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0].toUpperCase())
        .join("");
    const poolNameOf = (cdt0: string, index: number, count: number): string => {
      const suffix = count > 1 ? ` pool ${index + 1}` : " pool";
      const full = sanitizeCdtValue(`${cdt0}${suffix}`);
      return full.length <= CDT0_NAME_BUDGET
        ? full
        : sanitizeCdtValue(`${initialsOf(cdt0)}${suffix}`);
    };
    for (const cdt0 of byCdt0.keys()) {
      const candidates = [...pairViability.values()]
        .filter((viability) => viability.cdt0 === cdt0 && viability.subFloor && !viability.exempt)
        .filter(poolable)
        .sort(
          (a, b) =>
            rankIn(a.items.map((item) => item.skuId)) - rankIn(b.items.map((item) => item.skuId)),
        );
      if (candidates.length === 0) continue;
      const host = dominantOf.get(cdt0);
      if (candidates.length === 1 && host && host !== candidates[0].cdt1) {
        const guest = candidates[0];
        const hostColumn = pairViability.get(pairKeyOf(cdt0, host));
        pools[pairKeyOf(cdt0, host)] = {
          cdt0,
          cdt1: host,
          host,
          members: [
            {
              cdt1: host,
              skuIds: hostColumn?.items.map((item) => item.skuId) ?? [],
              widthShare: Math.round((hostColumn?.widthShare ?? 1) * 100) / 100,
              reason: "host: the stratum's dominant brand keeps its own column and labels",
            },
            {
              cdt1: guest.cdt1,
              skuIds: guest.items.map((item) => item.skuId),
              widthShare: Math.round(guest.widthShare * 100) / 100,
              reason: `${guest.items.length === 1 ? "one-SKU column; " : ""}${Math.round(guest.widthShare * 100)}% of "${cdt0}"'s mean column width (floor ${SUB_FLOOR_SHARE * 100}%); the only sub-floor brand here, so it joins "${host}" as its own block`,
            },
          ],
        };
        poolOfPair.set(pairKeyOf(cdt0, guest.cdt1), host);
        advisories.push(
          `"${cdt0}" seats "${guest.cdt1}" inside "${host}" as its own block (rule 2: a lone sub-floor brand cannot stand as a one-brand stratum).`,
        );
        continue;
      }
      if (candidates.length < 2) continue;
      const poolCount = Math.ceil(candidates.length / POOL_CAP);
      const poolSize = Math.ceil(candidates.length / poolCount);
      for (let index = 0; index < poolCount; index += 1) {
        const members = candidates.slice(index * poolSize, (index + 1) * poolSize);
        const name = poolNameOf(cdt0, index, poolCount);
        pools[name] = {
          cdt0,
          cdt1: name,
          members: members.map((member) => ({
            cdt1: member.cdt1,
            skuIds: member.items.map((item) => item.skuId),
            widthShare: Math.round(member.widthShare * 100) / 100,
            reason: `${member.items.length === 1 ? "one-SKU column; " : ""}${Math.round(member.widthShare * 100)}% of "${cdt0}"'s mean column width (floor ${SUB_FLOOR_SHARE * 100}%); sub-floor in "${cdt0}"`,
          })),
        };
        for (const member of members) poolOfPair.set(pairKeyOf(cdt0, member.cdt1), name);
        advisories.push(
          `"${cdt0}" pools ${members.map((member) => `"${member.cdt1}"`).join(", ")} into cdt1 "${name}" (each under ${SUB_FLOOR_SHARE * 100}% of the mean column width here; members stay whole, in wall order, brand-prefixed inside).`,
        );
      }
    }
  }

  {
    const stratumOf = new Map(strataResult.strata.map((stratum) => [stratum.cdt0, stratum]));
    const itemById = new Map(items.map((item) => [item.skuId, item]));
    const itemsOfStratum = (cdt0: string): StudioItem[] =>
      (stratumOf.get(cdt0)?.skuIds ?? [])
        .map((skuId) => itemById.get(skuId))
        .filter((item): item is StudioItem => !!item);
    const extentOf = (
      skuItems: readonly StudioItem[],
    ): { min: number; max: number; centre: number; maxStart: number } => {
      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;
      let weighted = 0;
      let weight = 0;
      // Where the stratum's block STARTS on each row: pogger's sequencing
      // compares starts (a later cdt0 starts at or after an earlier one, and
      // its rightmost start is at or past the earlier one's), never ends.
      const rowStart = new Map<number, number>();
      for (const item of skuItems) {
        for (const placement of item.placements) {
          min = Math.min(min, placement.xCm);
          max = Math.max(max, placement.xCm + placement.widthCm);
          weighted += (placement.xCm + placement.widthCm / 2) * placement.widthCm;
          weight += placement.widthCm;
          rowStart.set(
            placement.shelf,
            Math.min(rowStart.get(placement.shelf) ?? Number.POSITIVE_INFINITY, placement.xCm),
          );
        }
      }
      return {
        min,
        max,
        centre: weight > 0 ? weighted / weight : (min + max) / 2,
        maxStart: rowStart.size > 0 ? Math.max(...rowStart.values()) : min,
      };
    };
    const familyOf = (cdt0: string): string => stratumOf.get(cdt0)?.section ?? cdt0;
    const families = new Map<string, string[]>();
    for (const cdt0 of priorityCdt0Order) {
      if (!stratumOf.has(cdt0)) continue;
      const family = familyOf(cdt0);
      (families.get(family) ?? families.set(family, []).get(family)!).push(cdt0);
    }
    for (const [family, familyCdt0s] of families) {
      if (familyCdt0s.length < 2) continue;
      const rowsOf = (cdt0: string) => new Set(stratumOf.get(cdt0)?.homeShelves ?? []);
      const widthOf = (cdt0: string) => widthOfItems(itemsOfStratum(cdt0));
      const familyExtent = extentOf(familyCdt0s.flatMap(itemsOfStratum));
      const leftEdge: string[] = [];
      const rightEdge: string[] = [];
      const anchorless: string[] = [];
      /** Half a bay: two strata starting inside the same bay start together. */
      const START_TOLERANCE_CM = (runs[0]?.bayWidthCm ?? 0) / 2;
      for (const cdt0 of familyCdt0s) {
        const rows = rowsOf(cdt0);
        if (rows.size === 0 || itemsOfStratum(cdt0).length === 0) continue;
        const others = familyCdt0s.filter(
          (other) => other !== cdt0 && itemsOfStratum(other).length > 0,
        );
        const coverers = others.filter((other) => [...rowsOf(other)].some((row) => rows.has(row)));
        const covered = [...rows].every((row) => coverers.some((other) => rowsOf(other).has(row)));
        if (!covered || coverers.length === 0) continue;
        if (!coverers.every((other) => widthOf(cdt0) < widthOf(other))) continue;
        /* Which edge the sequencing can hold it at. Leading: it starts at or
           before every band covering its rows (an earlier cdt0 may start
           first). Trailing: its rightmost row start is at or past theirs (a
           later cdt0's rightmost start may not fall short). A slab that does
           neither stands mid-span, which the hard x-sequence cannot say: it
           stays in the order where the walk put it and loses its own-bay
           anchors, so the solver is free to seat it (run_ab117_opts was
           INFEASIBLE on exactly this, 2026-09-22). */
        const own = extentOf(itemsOfStratum(cdt0));
        if (!startOrdered) {
          const left = own.centre < (familyExtent.min + familyExtent.max) / 2;
          (left ? leftEdge : rightEdge).push(cdt0);
          advisories.push(
            `"${cdt0}" is a band nested inside ${coverers.map((other) => `"${other}"`).join(" + ")}'s rows (home ${[...rows].sort((a, b) => a - b).join(",")}, ${lengthLabel(widthOf(cdt0), lengthUnit)}, x-centre ${lengthLabel(own.centre, lengthUnit)} in the ${left ? "left" : "right"} half of "${family}"'s ${lengthLabel(familyExtent.min, lengthUnit)}–${lengthLabel(familyExtent.max, lengthUnit)}): its pairs take the family's ${left ? "leading" : "trailing"} edge so no other band continues past it on a shared row.`,
          );
          continue;
        }
        const coverExtents = coverers.map((other) => extentOf(itemsOfStratum(other)));
        const leading =
          own.min <= Math.min(...coverExtents.map((extent) => extent.min)) + START_TOLERANCE_CM;
        const trailing =
          own.maxStart >=
          Math.max(...coverExtents.map((extent) => extent.maxStart)) - START_TOLERANCE_CM;
        const where = `(home ${[...rows].sort((a, b) => a - b).join(",")}, ${lengthLabel(widthOf(cdt0), lengthUnit)}, starts ${lengthLabel(own.min, lengthUnit)}–${lengthLabel(own.maxStart, lengthUnit)} against the coverers' ${lengthLabel(Math.min(...coverExtents.map((extent) => extent.min)), lengthUnit)}–${lengthLabel(Math.max(...coverExtents.map((extent) => extent.maxStart)), lengthUnit)} in "${family}")`;
        if (!leading && !trailing) {
          anchorless.push(cdt0);
          advisories.push(
            `"${cdt0}" is a band nested inside ${coverers.map((other) => `"${other}"`).join(" + ")}'s rows ${where} and stands mid-span: pogger's cdt0 sequence cannot seat a block inside another's span, so it keeps its walk position and carries no own-bay anchor (lossy: the solver chooses where in "${family}" it stands).`,
          );
          continue;
        }
        const left =
          leading && trailing ? own.centre < (familyExtent.min + familyExtent.max) / 2 : leading;
        (left ? leftEdge : rightEdge).push(cdt0);
        advisories.push(
          `"${cdt0}" is a band nested inside ${coverers.map((other) => `"${other}"`).join(" + ")}'s rows ${where}: its pairs take the family's ${left ? "leading" : "trailing"} edge so no other band continues past it on a shared row.`,
        );
      }
      if (anchorless.length > 0) {
        const dropped = new Set(anchorless);
        strataResult = {
          ...strataResult,
          strata: strataResult.strata.map((stratum) =>
            // Every mid-span band, not only slabs: a slab's own-bay anchor
            // never emits, a band's zone-edge one does.
            dropped.has(stratum.cdt0)
              ? {
                  ...stratum,
                  anchors: false,
                  ...(stratum.anchorOwnBay ? { anchorOwnBay: false } : {}),
                }
              : stratum,
          ),
        };
        for (const stratum of strataResult.strata) stratumOf.set(stratum.cdt0, stratum);
      }
      // Several slabs on one edge stand in start order: the leading ones by
      // where they begin, the trailing ones by their rightmost start.
      if (startOrdered) {
        leftEdge.sort((a, b) => extentOf(itemsOfStratum(a)).min - extentOf(itemsOfStratum(b)).min);
        rightEdge.sort(
          (a, b) => extentOf(itemsOfStratum(a)).maxStart - extentOf(itemsOfStratum(b)).maxStart,
        );
      }
      if (leftEdge.length === 0 && rightEdge.length === 0) continue;
      const middle = familyCdt0s.filter(
        (cdt0) => !leftEdge.includes(cdt0) && !rightEdge.includes(cdt0),
      );
      const reordered = [...leftEdge, ...middle, ...rightEdge];
      const slots = priorityCdt0Order
        .map((cdt0, index) => (familyCdt0s.includes(cdt0) ? index : -1))
        .filter((index) => index >= 0);
      slots.forEach((slot, index) => {
        priorityCdt0Order[slot] = reordered[index];
      });
    }
  }

  const pairs = pairPriorities(items, walk, cdt0Of, cdt1Of, priorityCdt0Order, sectionOf);

  const treeItems: Record<string, ReplicaTreeItem> = {};
  const ruleReports: ReplicaOrderingRuleReport[] = [];
  const rowHomes: ReplicaRowHome[] = [];

  const ruleNodes: RuleNode[] = [];

  for (const cdt0 of pairs.cdt0Order) {
    const branchItems = byCdt0.get(cdt0) ?? [];
    const nodeCandidates = nodeCandidatesOf(cdt0);

    const nodeColumns: { cdt1: string; columnItems: StudioItem[]; brandPrefix: string | null }[] =
      [];
    for (const [cdt1, columnItems] of columnsOf(branchItems)) {
      const pool = Object.values(pools).find((entry) => entry.cdt0 === cdt0 && entry.cdt1 === cdt1);
      if (!pool) {
        nodeColumns.push({ cdt1, columnItems, brandPrefix: null });
        continue;
      }
      const byMember = new Map<string, StudioItem[]>();
      for (const item of columnItems) {
        const brand = rawCdt1Of(item);
        (byMember.get(brand) ?? byMember.set(brand, []).get(brand)!).push(item);
      }
      for (const [brand, memberItems] of [...byMember.entries()].sort(
        (a, b) => rankIn(a[1].map((item) => item.skuId)) - rankIn(b[1].map((item) => item.skuId)),
      )) {
        nodeColumns.push({
          cdt1,
          columnItems: memberItems,
          brandPrefix: brand === pool.host ? null : brand,
        });
      }
    }

    for (const { cdt1, columnItems, brandPrefix } of nodeColumns) {
      const tree = inferTree(columnItems, runs, nodeCandidates, attrValueOf);
      if (!tree) {
        advisories.push(
          `"${cdt0} › ${cdt1}" has no attribute that splits on the wall; one unnamed block.`,
        );
      }

      const itemsOfNode = new Map<TreeNode, StudioItem[]>();
      const assign = (node: TreeNode | null, nodeItems: StudioItem[]): void => {
        if (!node) return;
        itemsOfNode.set(node, nodeItems);
        for (const kid of node.kids) {
          assign(
            kid.node,
            nodeItems.filter((item) => attrValueOf(item, node.attr) === kid.value),
          );
        }
      };
      assign(tree, columnItems);
      const kidItemsOf = (node: TreeNode, value: string): StudioItem[] =>
        kidItemsIn(itemsOfNode.get(node) ?? [], node, value);

      const multiRowBlock = (skuItems: readonly StudioItem[]): boolean => {
        const perShelf = new Map<number, number>();
        for (const item of skuItems) {
          const shelf = rowOf(item);
          if (shelf !== undefined) perShelf.set(shelf, (perShelf.get(shelf) ?? 0) + 1);
        }
        return [...perShelf.values()].filter((count) => count >= 2).length >= 2;
      };
      const windowSplit = (node: TreeNode): boolean => {
        const multi = node.kids.filter(
          (kid) => kid.count >= 2 && multiRowBlock(kidItemsOf(node, kid.value)),
        ).length;
        if (multi === 0) return false;

        return !(node.kids.length === 2 && multi === 1);
      };

      const rowPartition = (node: TreeNode): boolean =>
        isRowPartition(node, itemsOfNode.get(node) ?? []);

      const partsCache = new Map<TreeNode, Map<string, string | null>>();
      const partsOverKids = (kids: TreeNode["kids"]): Map<string, string | null> => {
        const parts = new Map<string, string | null>();
        if (kids.every((kid) => kid.count <= 1)) {
          for (const kid of kids) parts.set(kid.value, null);
        } else {
          let index = 0;
          while (index < kids.length) {
            if (kids[index].count >= 2) {
              parts.set(kids[index].value, kids[index].value);
              index += 1;
              continue;
            }
            let end = index;
            while (end < kids.length && kids[end].count <= 1) end += 1;
            const run = kids.slice(index, end);
            const live = run.map((kid) => kid.value).filter((value) => value !== NONE_VALUE);
            const part = run.length === 1 ? run[0].value : live.length > 0 ? live.join("/") : null;
            for (const kid of run) parts.set(kid.value, part);
            index = end;
          }
        }
        return parts;
      };
      const cdt3PartsOf = (node: TreeNode): Map<string, string | null> => {
        const cached = partsCache.get(node);
        if (cached) return cached;
        const parts = partsOverKids(node.kids);
        partsCache.set(node, parts);
        return parts;
      };
      const reported = new Set<TreeNode>();
      const describeNode = (node: TreeNode): string =>
        `${node.attr} [${node.kind} ${node.reading}${node.direction} ${(node.purity * 100).toFixed(0)}% pure, ${node.gain.toFixed(2)} bits]`;

      interface ItemPath {
        cdt2Parts: string[];
        cdt2Attrs: string[];
        cdt3Parts: string[];
        cdt3Nodes: TreeNode[];
        steps: ReplicaPathStep[];
      }

      const isLadder = (node: TreeNode): boolean =>
        node.direction !== "" && cutBaseOfAttr(node.attr) === null;
      const singleRow = new Set(columnItems.map(rowOf)).size <= 1;
      const allNodes: TreeNode[] = [];
      const collect = (node: TreeNode | null): void => {
        if (!node) return;
        allNodes.push(node);
        for (const kid of node.kids) collect(kid.node);
      };
      collect(tree);
      const hasWindow =
        !singleRow &&
        allNodes.some(
          (node) =>
            !(node.reading === "vertical" && rowPartition(node)) &&
            windowSplit(node) &&
            !isLadder(node),
        );
      const cdt2Mode: "root" | "windows" | "first" = singleRow
        ? "root"
        : hasWindow
          ? "windows"
          : "first";

      const ladderGroups = new Map<TreeNode, Map<string, string>>();
      const ladderGroupPart = (node: TreeNode, value: string): string => {
        let groups = ladderGroups.get(node);
        if (!groups) {
          groups = new Map();
          const rungs = node.kids.map((kid) => {
            const rows = kidItemsOf(node, kid.value)
              .map(rowOf)
              .filter((row): row is number => row !== undefined);
            return {
              value: kid.value,
              row: rows.length > 0 ? Math.min(...rows) : Number.MAX_SAFE_INTEGER,
            };
          });
          let start = 0;
          for (let index = 1; index <= rungs.length; index += 1) {
            if (index === rungs.length || rungs[index].row !== rungs[start].row) {
              const run = rungs.slice(start, index);
              const label =
                run.length === 1 ? run[0].value : `${run[0].value}-${run[run.length - 1].value}`;
              for (const rung of run) groups.set(rung.value, label);
              start = index;
            }
          }
          ladderGroups.set(node, groups);
        }
        return groups.get(value) ?? value;
      };

      const ruleTreeOf = (
        node: TreeNode | null,
        phase: "top" | "cdt2" | "cdt3",
      ): RuleTreeNode | null => {
        if (!node) return null;
        const partition = node.reading === "vertical" && rowPartition(node);
        let carrier: RuleCarrier;
        let next = phase;
        let ladderGroups: Record<string, string> | undefined;
        if (partition) {
          carrier = phase === "top" ? "rowHome" : "lossy";
          if (phase !== "top") next = "cdt3";
        } else {
          const takeAsCdt2 =
            cdt2Mode === "windows"
              ? phase !== "cdt3" && windowSplit(node) && !isLadder(node)
              : phase === "top";
          if (takeAsCdt2 && isLadder(node) && !singleRow) {
            carrier = "ladderCdt2";
            ladderGroups = Object.fromEntries(
              node.kids.map((kid) => [kid.value, ladderGroupPart(node, kid.value)]),
            );
            next = "cdt3";
          } else if (takeAsCdt2) {
            carrier = "cdt2";
            next = "cdt2";
          } else {
            carrier = "cdt3";
            next = "cdt3";
          }
        }
        return {
          attr: node.attr,
          carrier,
          describe: describeNode(node),
          direction: node.direction,
          ...(ladderGroups ? { ladderGroups } : {}),
          kids: node.kids.map((kid) => ({
            value: kid.value,
            count: kid.count,
            node: ruleTreeOf(kid.node, next),
          })),
        };
      };
      ruleNodes.push({ cdt0, cdt1: brandPrefix ?? cdt1, tree: ruleTreeOf(tree, "top") });

      let pathBreakReported = false;
      const pathOf = (item: StudioItem): ItemPath => {
        const path: ItemPath = {
          cdt2Parts: brandPrefix !== null ? [brandPrefix] : [],
          cdt2Attrs: brandPrefix !== null ? [cdt1AttrOf.get(cdt0) ?? "brand"] : [],
          cdt3Parts: [],
          cdt3Nodes: [],
          steps:
            brandPrefix !== null
              ? [
                  {
                    attr: cdt1AttrOf.get(cdt0) ?? "brand",
                    value: attrValueOf(item, cdt1AttrOf.get(cdt0) ?? "brand"),
                    carrier: "cdt2",
                    part: brandPrefix,
                  },
                ]
              : [],
        };
        let node: TreeNode | null = tree;
        let phase: "top" | "cdt2" | "cdt3" = "top";
        while (node) {
          const value = attrValueOf(item, node.attr);
          const kid = node.kids.find((entry) => entry.value === value);
          if (!kid) {
            // Stop rather than invent a branch when source and inferred values disagree.

            if (!pathBreakReported) {
              pathBreakReported = true;
              fail(
                "path-broken",
                `"${cdt0} › ${cdt1}": ${item.name.slice(0, 40)} reads "${value}" for ${node.attr}, which is none of the split's values [${node.kids.map((entry) => entry.value).join(", ")}] — the label stops at this split.`,
              );
            }
            break;
          }
          const partition = node.reading === "vertical" && rowPartition(node);
          const step = (carrier: ReplicaPathStep["carrier"], part: string | null): void => {
            path.steps.push({ attr: node!.attr, value, carrier, part });
          };
          if (partition && phase === "top") {
            if (!reported.has(node)) {
              reported.add(node);
              let recorded = 0;
              for (const entry of node.kids) {
                const entryItems = kidItemsOf(node, entry.value);
                const rule: ReplicaRowHome = {
                  cdt0,
                  cdt1,
                  attr: node.attr,
                  value: entry.value,
                  shelves: shelvesOf(entryItems),
                  skuIds: entryItems.map((entryItem) => entryItem.skuId),
                  shipped: false,
                };
                if (privateRowHomeKeys.has(rowHomeKeyOf(rule))) continue;
                privateRowHomeKeys.add(rowHomeKeyOf(rule));
                privateRowHomes.push(rule);
                recorded += 1;
              }
              advisories.push(
                `"${cdt0} › ${cdt1}" opens on a brand-private row partition, ${describeNode(node)} — no other brand of "${cdt0}" follows it, so it is not carved into a cdt0 (§9: upward only when shared); folded into the cdt3 label, lossy${recorded > 0 ? "" : " (recorded by the 2b pre-pass)"}.`,
              );
            }
            path.cdt3Parts.push(value);
            path.cdt3Nodes.push(node);
            step("rowHome", value);
          } else if (partition) {
            if (!reported.has(node)) {
              reported.add(node);
              advisories.push(
                `"${cdt0} › ${cdt1}" has a vertical split UNDER a horizontal one (${describeNode(node)}); pogger can only approximate it by fill order — folded into the cdt3 label, lossy.`,
              );
            }
            phase = "cdt3";
            path.cdt3Parts.push(value);
            path.cdt3Nodes.push(node);
            step("lossy", value);
          } else {
            if (node.reading === "vertical" && !reported.has(node)) {
              reported.add(node);
              advisories.push(
                `"${cdt0} › ${cdt1}" reads ${describeNode(node)} as vertical, but only one value is a multi-item block (the rest are seam singles), so it owns every row of the node — not a row partition; read as a horizontal split.`,
              );
            }
            const takeAsCdt2 =
              cdt2Mode === "windows"
                ? phase !== "cdt3" && windowSplit(node) && !isLadder(node)
                : phase === "top";
            if (takeAsCdt2) {
              phase = "cdt2";
              if (isLadder(node) && !singleRow) {
                const group = ladderGroupPart(node, value);
                path.cdt2Parts.push(group);
                path.cdt2Attrs.push(node.attr);
                step("cdt2", group);

                const ladderNode: TreeNode = node;
                const groupKids = ladderNode.kids.filter(
                  (kid) => ladderGroupPart(ladderNode, kid.value) === group,
                );
                const rung = partsOverKids(groupKids).get(value) ?? null;
                if (rung !== null) path.cdt3Parts.push(rung);
                path.cdt3Nodes.push(node);
                step(rung === null ? "seq" : "cdt3", rung);
                phase = "cdt3";
              } else {
                const part = value === NONE_VALUE ? `no ${node.attr}` : value;
                path.cdt2Parts.push(part);
                path.cdt2Attrs.push(node.attr);
                step("cdt2", part);
              }
            } else {
              phase = "cdt3";
              const part = cdt3PartsOf(node).get(value) ?? null;
              if (part !== null) path.cdt3Parts.push(part);
              path.cdt3Nodes.push(node);
              step(part === null ? "seq" : "cdt3", part);
            }
          }
          node = kid.node;
        }
        return path;
      };

      const treeRank = new Map<string, number>();
      const visit = (node: TreeNode | null, nodeItems: readonly StudioItem[]): void => {
        if (!node) {
          for (const item of [...nodeItems].sort((a, b) => rankIn([a.skuId]) - rankIn([b.skuId]))) {
            if (!treeRank.has(item.skuId)) treeRank.set(item.skuId, treeRank.size);
          }
          return;
        }
        for (const kid of node.kids) visit(kid.node, kidItemsOf(node, kid.value));
      };
      visit(tree, columnItems);
      visit(null, columnItems);
      const treeRankIn = (skuItems: readonly StudioItem[]): number =>
        Math.min(...skuItems.map((item) => treeRank.get(item.skuId) ?? Number.MAX_SAFE_INTEGER));

      const paths = new Map(columnItems.map((item) => [item.skuId, pathOf(item)]));
      if (
        tree &&
        columnItems.length > 1 &&
        [...paths.values()].every(
          (path) => path.cdt2Parts.length === (brandPrefix !== null ? 1 : 0),
        )
      ) {
        advisories.push(
          `"${cdt0} › ${cdt1}${brandPrefix !== null ? ` › ${brandPrefix}` : ""}": cdt2 repeats cdt1 — no horizontal split in the node (its only split is a row partition, folded into cdt3).`,
        );
      }
      const byBlock = new Map<string, StudioItem[]>();
      for (const item of columnItems) {
        const blockValue = composeLabel(paths.get(item.skuId)!.cdt2Parts, cdt1);
        (byBlock.get(blockValue) ?? byBlock.set(blockValue, []).get(blockValue)!).push(item);
      }
      const blocks = [...byBlock.entries()].sort((a, b) => treeRankIn(a[1]) - treeRankIn(b[1]));

      blocks.forEach(([blockValue, blockItems], blockIdx) => {
        const labelOf = (item: StudioItem): string =>
          composeLabel(paths.get(item.skuId)!.cdt3Parts, blockValue);
        const labelRank = new Map<string, number>();
        for (const item of blockItems) {
          const label = labelOf(item);
          const rank = treeRankIn([item]);
          if (!labelRank.has(label) || rank < labelRank.get(label)!) labelRank.set(label, rank);
        }
        const orderedLabels = [...labelRank.keys()].sort(
          (a, b) => labelRank.get(a)! - labelRank.get(b)!,
        );
        const rankOfLabel = new Map(orderedLabels.map((label, index) => [label, index + 1]));

        const chainNodes: TreeNode[] = [];
        for (const item of blockItems) {
          for (const node of paths.get(item.skuId)!.cdt3Nodes)
            if (!chainNodes.includes(node)) chainNodes.push(node);
        }
        const leadItem = [...blockItems].sort((a, b) => rankIn([a.skuId]) - rankIn([b.skuId]))[0];
        const leadPath = paths.get(leadItem.skuId)!;
        const ruleDescription =
          chainNodes.length > 0
            ? chainNodes.map(describeNode).join(" then ")
            : "wall order (no further attribute varies)";

        if (blockItems.length > 1 && orderedLabels.length <= 1) {
          const spent = new Set([
            ...leadPath.cdt2Attrs,
            ...leadPath.cdt3Nodes.map((node) => node.attr),
          ]);
          const stillVarying = nodeCandidates.filter((candidate) => {
            if (spent.has(candidate)) return false;
            const values = new Set(blockItems.map((item) => attrValueOf(item, candidate)));
            return values.size > 1;
          });
          if (stillVarying.length > 0) {
            advisories.push(
              `"${cdt0} › ${cdt1} › ${blockValue}" carries ${blockItems.length} SKUs with cdt3 constant — [${stillVarying.join(", ")}] still vary(s) but none cleared the 75% purity floor (or only as singleton leaves, which ride walk_rank); needs a ruling only if the wall disagrees.`,
            );
          } else {
            warn(
              "constant-tiers",
              `"${cdt0} › ${cdt1} › ${blockValue}" carries ${blockItems.length} SKUs with cdt2 AND cdt3 both constant — nothing in the candidate attributes distinguishes them.`,
            );
          }
        }

        const firstCdt3 = leadPath.cdt3Nodes[0];
        ruleReports.push({
          cdt0,
          cdt1,
          cdt2: blockValue,
          cdt2Attr: leadPath.cdt2Attrs.length > 0 ? leadPath.cdt2Attrs.join(" — ") : null,
          block_order: blockIdx + 1,
          rule: ruleDescription,
          attr: firstCdt3?.attr ?? null,
          direction: (firstCdt3?.direction.trim() || "ascending") as "ascending" | "descending",
          labels: orderedLabels,
          pinnedLast: [],
        });

        const pair = pairs.pairs.find((entry) => entry.cdt0 === cdt0 && entry.cdt1 === cdt1);
        for (const item of blockItems) {
          const label = labelOf(item);
          treeItems[item.skuId] = {
            cdt0,
            cdt1,
            cdt2: blockValue,
            cdt3: label,
            cdt1_priority: pairs.priorityOf.get(pairKeyOf(cdt0, cdt1)) ?? 0,
            cdt2_priority: blockIdx + 1,
            cdt3_priority: rankOfLabel.get(label) ?? Math.max(orderedLabels.length, 1),
            segment: pair?.segment ?? cdt1,
            walk_rank: walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER,
            path: paths.get(item.skuId)!.steps,
          };
        }
      });
    }
  }

  {
    const nodeSize = new Map<string, number>();
    for (const entry of Object.values(treeItems)) {
      const key = pairKeyOf(entry.cdt0, entry.cdt1);
      nodeSize.set(key, (nodeSize.get(key) ?? 0) + 1);
    }
    const blockSize = new Map<string, number>();
    for (const entry of Object.values(treeItems)) {
      const key = [entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR);
      blockSize.set(key, (blockSize.get(key) ?? 0) + 1);
    }
    let cdt2RepeatsOneItem = 0;
    let cdt2RepeatsMulti = 0;
    let cdt3RepeatsOneItem = 0;
    let cdt3RepeatsMulti = 0;
    for (const entry of Object.values(treeItems)) {
      if (entry.cdt2 === entry.cdt1) {
        if ((nodeSize.get(pairKeyOf(entry.cdt0, entry.cdt1)) ?? 0) <= 1) cdt2RepeatsOneItem += 1;
        else cdt2RepeatsMulti += 1;
      }
      if (entry.cdt3 === entry.cdt2) {
        if ((blockSize.get([entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR)) ?? 0) <= 1)
          cdt3RepeatsOneItem += 1;
        else cdt3RepeatsMulti += 1;
      }
    }
    advisories.push(
      `Level repeats: cdt2 == cdt1 on ${cdt2RepeatsOneItem} item(s) in one-item nodes and ${cdt2RepeatsMulti} in multi-item nodes (the latter only where the node has no horizontal split); cdt3 == cdt2 on ${cdt3RepeatsOneItem} item(s) in one-item blocks and ${cdt3RepeatsMulti} in multi-item blocks with no split remaining.`,
    );
  }

  for (const pool of Object.values(pools)) {
    const entries = Object.values(treeItems).filter(
      (entry) => entry.cdt0 === pool.cdt0 && entry.cdt1 === pool.cdt1,
    );
    const firstRankOfBlock = new Map<string, number>();
    for (const entry of entries) {
      if (entry.walk_rank < (firstRankOfBlock.get(entry.cdt2) ?? Number.MAX_SAFE_INTEGER)) {
        firstRankOfBlock.set(entry.cdt2, entry.walk_rank);
      }
    }
    const orderOfBlock = new Map(
      [...firstRankOfBlock.entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([cdt2], index) => [cdt2, index + 1]),
    );
    for (const entry of entries)
      entry.cdt2_priority = orderOfBlock.get(entry.cdt2) ?? entry.cdt2_priority;
    for (const report of ruleReports) {
      if (report.cdt0 === pool.cdt0 && report.cdt1 === pool.cdt1) {
        report.block_order = orderOfBlock.get(report.cdt2) ?? report.block_order;
      }
    }
  }

  const labelPriorities = new Map<string, Map<string, number>>();
  for (const entry of Object.values(treeItems)) {
    const node = [entry.cdt0, entry.cdt1, entry.cdt2].join(PATH_SEPARATOR);
    const seen = labelPriorities.get(node) ?? labelPriorities.set(node, new Map()).get(node)!;
    const existing = seen.get(entry.cdt3);
    if (existing !== undefined && existing !== entry.cdt3_priority) {
      fail(
        "cdt3-label-priority",
        `"${node}" gives label "${entry.cdt3}" both priority ${existing} and ${entry.cdt3_priority}; pogger requires one priority per LABEL.`,
      );
    }
    seen.set(entry.cdt3, entry.cdt3_priority);
  }
  const priorityNumbers = [...new Set(pairs.pairs.map((pair) => pair.priority))].sort(
    (a, b) => a - b,
  );
  if (!priorityNumbers.every((number, index) => number === index + 1)) {
    fail(
      "cdt1-priority-gap",
      `pair priorities run ${priorityNumbers[0]}..${priorityNumbers[priorityNumbers.length - 1]} over ${priorityNumbers.length} pairs, with gaps.`,
    );
  }

  const homed = withRowHomeStrata(strataResult, rowHomes);
  for (const warning of homed.rowHomeWarnings) advisories.push(warning);
  const shippedCdt0s = new Set(Object.values(treeItems).map((entry) => entry.cdt0));
  const shippedRowHomeStrata = homed.rowHomeStrata.filter((stratum) =>
    shippedCdt0s.has(stratum.cdt0),
  );
  for (const stratum of homed.rowHomeStrata) {
    if (shippedCdt0s.has(stratum.cdt0)) continue;
    warn(
      "row-home-unshipped",
      `"${stratum.cdt0}" (rows ${stratum.homeShelves.join(",")}, ${stratum.skuIds.length} SKUs of "${stratum.section}") is a vertical split the tree carries under cdt1, not as a cdt0: its row-home is reported on restrictions.rowHomeStrata and NOT emitted as rows — adopt the sub-stratum (withRowHomeStrata) before priorities are numbered to bind it.`,
    );
  }

  // Restrictions may name only CDT0 values present in the shipped tree.
  const emittedStrata = [...strataResult.strata, ...shippedRowHomeStrata].filter(
    (stratum) => stratum.skuIds.length > 0,
  );

  /* Zone CANDIDATES, not this wall's merge (leak L1): on 117 bleach's 20.5%
     earns one bay it cannot hold, so step 1's rules merge it into stain
     removers — a 7-bay rounding artefact. The rule set carries the sections
     apart with `interleaveWith`, and the fixture side re-decides the merge
     per store. 117 reproduces the golden on either path. Step 8 ships this as
     `rules.partition`; `zonesFromFixtureSide` also zones THIS wall with it.
     The merchant's own shipped section order wins when the caller sent one
     (`shippedZones`), so each child fixture still decides its own seam. */
  const zoneCandidates =
    shippedZones ??
    deriveZoneShareRules(items, sectionAttr, runs.length, runs[0], {
      merge: false,
      lengthUnit,
    });
  const itemOfSku = new Map(items.map((item) => [item.skuId, item]));
  /** Each stratum's product width at BASE facings — what the wall holds. */
  const baseDemandCmOf: [string, number][] = emittedStrata.map((stratum) => [
    stratum.cdt0,
    stratum.skuIds.reduce(
      (sum, skuId) =>
        sum + (itemOfSku.get(skuId)?.placements.reduce((own, p) => own + p.widthCm, 0) ?? 0),
      0,
    ),
  ]);
  /** Each stratum's product width at ONE facing each — what must fit. */
  const minDemandCmOf: [string, number][] = emittedStrata.map((stratum) => [
    stratum.cdt0,
    stratum.skuIds.reduce((sum, skuId) => sum + (itemOfSku.get(skuId)?.facingWidthCm ?? 0), 0),
  ]);
  /* Carves (§2.3): each cdt0 sub-stratum with the side it carries and the
     parent it folds back into when the store's band has fewer than 2 rows. */
  const carves: RuleCarve[] = [];
  {
    const sidesOfParent = new Map<string, { side: string; top: number }[]>();
    for (const stratum of preHomed.rowHomeStrata) {
      const rule = preRowHomes.find((entry) => entry.skuIds.includes(stratum.skuIds[0] ?? ""));
      const parentCdt0 = parentOfSubStratum.get(stratum.cdt0);
      if (!rule || !parentCdt0) continue;
      /* A stacked brand (labelled rule) is a slab inside its parent, not a
         side of a shared row partition: it carries `parentCdt0` on its
         stratum and the fixture side nests it. As a carve side it would
         stand in for a size split that never existed, and a parent with
         exactly two slabs would be re-drafted as a carve parent beside the
         real band, doubling it and overwriting its rows. */
      if (rule.label !== undefined) continue;
      const list =
        sidesOfParent.get(parentCdt0) ?? sidesOfParent.set(parentCdt0, []).get(parentCdt0)!;
      if (!list.some((entry) => entry.side === rule.value)) {
        list.push({ side: rule.value, top: Math.min(...rule.shelves) });
      }
      carves.push({
        cdt0: stratum.cdt0,
        parentCdt0,
        attr: rule.attr,
        side: rule.value,
        sideOrder: 0,
      });
    }
    for (const carve of carves) {
      const ordered = (sidesOfParent.get(carve.parentCdt0) ?? [])
        .slice()
        .sort((a, b) => a.top - b.top)
        .map((entry) => entry.side);
      carve.sideOrder = Math.max(0, ordered.indexOf(carve.side));
    }
  }

  /* 6a-bis. `zonesFromFixtureSide`: the MATERIALIZER decides this wall's zones
     too. The zone candidates, the base's own fixture and the base's own
     section sales / demand go through `allocateZoneBays` — the same call
     `materializeStore` makes — and the bays it hands back (with the seams it
     lends) replace step 1's merged ones for the partition bans. `stratum.zone`
     indexes step 1's zones, so each stratum is re-homed by its SECTION, which
     is how the fixture side matches bands to zones as well. */
  /** The base wall as a STORE, exactly the way `materializeStore` builds one:
   *  the base's own uniform fixture, its section sales, its strata's demand at
   *  base and at one facing, its brand columns after pooling and its widest
   *  packs. Only the zone stage reads it. */
  const fixtureSideInputOf = (): FixtureSideInput => {
    const run = runs[0];
    const shelves = shelfSpace.shelfRanks.length;
    const baseCm = new Map(baseDemandCmOf);
    const minCm = new Map(minDemandCmOf);
    const sectionSales: Record<string, number> = {};
    for (const item of items) {
      // Keyed as zone sections are (`sectionShapes`): sanitized, or "Tide®" weighs nothing.
      const section = sectionNameOf(item, sectionAttr);
      sectionSales[section] = (sectionSales[section] ?? 0) + (item.salesAmount ?? 0);
    }
    const pairCountByStratum = new Map<string, number>();
    for (const pair of pairs.pairs) {
      pairCountByStratum.set(pair.cdt0, (pairCountByStratum.get(pair.cdt0) ?? 0) + 1);
    }
    return {
      rules: zoneCandidates,
      bands: deriveBandRules(emittedStrata.map(shelfSpace.stratum), {
        baseWidthCm: baseCm,
        minWidthCm: minCm,
        carves,
      }),
      // The plan's widest and tallest bay.
      fixture: uniformFixture(run?.bays ?? 0, shelves, {
        widthCm: run ? Math.max(...runs.map((each) => runBayWidthOf(each, runs))) : undefined,
        heightCm: run ? Math.max(...runs.map((each) => each.heightCm)) : undefined,
      }),
      sectionSales,
      lengthUnit,
      skuCountByStratum: new Map(
        emittedStrata.map((stratum) => [stratum.cdt0, stratum.skuIds.length]),
      ),
      demandByStratum: new Map(
        emittedStrata.map((stratum) => [
          stratum.cdt0,
          { minCm: minCm.get(stratum.cdt0) ?? 0, baseCm: baseCm.get(stratum.cdt0) ?? 0 },
        ]),
      ),
      pairCountByStratum,
      widestPackCmByStratum: new Map(
        emittedStrata.map((stratum) => [
          stratum.cdt0,
          stratum.skuIds.reduce(
            (widest, skuId) => Math.max(widest, itemOfSku.get(skuId)?.facingWidthCm ?? 0),
            0,
          ),
        ]),
      ),
      // The materializer's own reading of `fences.sharedSeams` (`openSeams`).
      ...(fences.sharedSeams ? { openSeams: true } : {}),
    };
  };
  const fixtureSideZones =
    zonesFromFixtureSide && (runs[0]?.bays ?? 0) > 0 && zoneCandidates.zones.length > 1
      ? allocateZoneBays(fixtureSideInputOf())
      : null;
  const zonedStrata = fixtureSideZones
    ? emittedStrata.map((stratum) => {
        const zone = fixtureSideZones.zones.findIndex((entry) =>
          entry.sections.includes(stratum.section),
        );
        return zone < 0 || zone === stratum.zone ? stratum : { ...stratum, zone };
      })
    : emittedStrata;
  const zonedPartition: BayPartitionResult = fixtureSideZones
    ? { zones: fixtureSideZones.zones, warnings: fixtureSideZones.warnings }
    : partition;
  const zoneOfSection = new Map<string, number>();
  zonedPartition.zones.forEach((zone, index) => {
    for (const section of zone.sections) zoneOfSection.set(section, index);
  });
  if (fixtureSideZones) {
    for (const warning of fixtureSideZones.warnings) advisories.push(`zones: ${warning}`);
    for (const zone of fixtureSideZones.zones) {
      advisories.push(
        `zones: "${zone.sections.join(" + ")}" holds bay(s) ${zone.bays.join(",") || "none"}${
          zone.sharedBays?.length ? ` and shares bay(s) ${zone.sharedBays.join(",")}` : ""
        } (fixture side, ${(100 * zone.share).toFixed(1)}% share).`,
      );
    }
  }

  const { rows, anchors } = materializeRestrictionRows(
    zonedStrata.map(shelfSpace.stratum),
    zonedPartition,
    shelfSpace.runs,
    shelfSpace.items(items),
    fences,
  );
  const rowStrata: Record<string, readonly number[]> = {};
  for (const stratum of emittedStrata) {
    rowStrata[stratum.cdt0] = shelfSpace.stratum(stratum).homeShelves;
  }

  {
    const run0 = runs[0];
    const widthOfSku = new Map(
      items.map((item) => [
        item.skuId,
        item.placements.reduce((sum, placement) => sum + placement.widthCm, 0),
      ]),
    );
    const totalWidth = [...widthOfSku.values()].reduce((sum, width) => sum + width, 0);
    const shelfCount = shelfSpace.shelfRanks.length;
    if (run0 && run0.bayWidthCm > 0 && shelfCount > 0 && totalWidth > 0) {
      for (const zone of zonedPartition.zones) {
        const zoneWidth = items
          .filter((item) => zone.sections.includes(sectionNameOf(item, sectionAttr)))
          .reduce((sum, item) => sum + (widthOfSku.get(item.skuId) ?? 0), 0);
        const capacity = zone.bays.length * run0.bayWidthCm * shelfCount;
        advisories.push(
          `space: zone "${zone.sections.join(" + ")}" sales share ${(100 * zone.share).toFixed(1)}% buys ${zone.bays.length} bay(s) = ${lengthLabel(capacity, lengthUnit)} of shelf for ${lengthLabel(zoneWidth, lengthUnit)} of product on the wall (fill ${((100 * zoneWidth) / capacity).toFixed(0)}%; width share ${((100 * zoneWidth) / totalWidth).toFixed(1)}%).`,
        );
      }
      for (const stratum of zonedStrata) {
        const bays = zonedPartition.zones[stratum.zone]?.bays ?? [];
        const supply = bays.length * run0.bayWidthCm * stratum.homeShelves.length;
        const demand = stratum.skuIds.reduce((sum, skuId) => sum + (widthOfSku.get(skuId) ?? 0), 0);
        if (!(supply > 0)) continue;
        advisories.push(
          `space: "${stratum.cdt0}" holds ${lengthLabel(demand, lengthUnit)} of product for ${lengthLabel(supply, lengthUnit)} of shelf (${bays.length} bay(s) × rows ${stratum.homeShelves.join(",")}) — fill ${((100 * demand) / supply).toFixed(0)}%${stratum.fullHeight ? " (no row fence; shares its rows)" : ""}.`,
        );
      }
    }
  }

  // Keep the legacy key although each anchor belongs to its zone's first bay.
  const bay1Anchors: Record<string, readonly number[]> = {};
  for (const anchor of anchors) {
    bay1Anchors[anchor.variant] = [...(bay1Anchors[anchor.variant] ?? []), Number(anchor.shelf)];
  }

  // Items the base pog itself places outside their zone's bays: legal (the
  // solver will re-home them), but the person reading the diff should know.
  if (zonedPartition.zones.length > 1) {
    let spilled = 0;
    for (const stratum of strataResult.strata) {
      const bays = new Set(
        zonedPartition.zones[zoneOfSection.get(stratum.section) ?? stratum.zone]?.bays ?? [],
      );
      for (const skuId of stratum.skuIds) {
        const first = walk.firstPlacement.get(skuId);
        if (first && !bays.has(first.bay)) spilled += 1;
      }
    }
    if (spilled > 0) {
      advisories.push(
        `${spilled} SKUs sit outside their partition zone on the base pog itself; the partition re-homes them.`,
      );
    }
  }

  const pins: Record<
    string,
    { min_facings: number; max_facings: number; min_days_of_supply: number }
  > = {};
  for (const item of items) {
    pins[item.skuId] = {
      min_facings: item.facings,
      max_facings: item.facings,
      min_days_of_supply: 0,
    };
  }

  // Pogger uses candidate order as the final tie-break within a label.
  const orderedTreeItems = Object.fromEntries(
    Object.entries(treeItems).sort((a, b) => a[1].walk_rank - b[1].walk_rank),
  );

  /* 7b. Zone seams: the run boundary between two adjacent zones. A shelf that
     merges across one would grow a zone's shelf into its neighbour's bay. A
     SHARED bay holds both zones' products, so that boundary is no seam at all —
     which is the fixture side's own rule, and why it reports the seams itself
     under `zonesFromFixtureSide`. */
  const zoneSeams: { leftRun: number; rightRun: number }[] = [];
  if (fixtureSideZones) {
    zoneSeams.push(...fixtureSideZones.zoneSeams.map((seam) => ({ ...seam })));
  } else {
    for (let index = 0; index + 1 < partition.zones.length; index += 1) {
      const own = partition.zones[index].bays;
      const next = partition.zones[index + 1].bays;
      const leftRun = own[own.length - 1];
      const rightRun = next[0];
      if (leftRun !== undefined && rightRun !== undefined) zoneSeams.push({ leftRun, rightRun });
    }
  }

  const rules: RuleSet = (() => {
    // Child stores re-evaluate pooling, so persist pair order by real brand.
    const rawPairs = pairPriorities(items, walk, cdt0Of, rawCdt1Of, priorityCdt0Order, sectionOf);
    const attrsSeenInTrees = new Set<string>();
    const walkTree = (node: RuleTreeNode | null): void => {
      if (!node) return;
      attrsSeenInTrees.add(node.attr);
      for (const kid of node.kids) walkTree(kid.node);
    };
    for (const node of ruleNodes) walkTree(node.tree);
    const sectionOfCdt0: Record<string, string> = {};
    const cdt1AttrOfCdt0: Record<string, string> = {};
    for (const item of items) {
      const cdt0 = cdt0Of(item);
      if (!(cdt0 in sectionOfCdt0)) sectionOfCdt0[cdt0] = sectionOf(item);
      if (!(cdt0 in cdt1AttrOfCdt0)) cdt1AttrOfCdt0[cdt0] = cdt1AttrOf.get(cdt0) ?? "";
    }
    const skus: RuleSkuFacts[] = items.map((item) => ({
      skuId: item.skuId,
      cdt0: cdt0Of(item),
      cdt1: rawCdt1Of(item),
      section: sectionOf(item),
      walkRank: walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER,
      baseFacings: item.facings,
      attrs: Object.fromEntries(
        [...attrsSeenInTrees].map((attr) => [attr, attrValueOf(item, attr)]),
      ),
    }));
    const basePools: RulePool[] = Object.entries(pools).map(([key, pool]) => ({
      key,
      cdt0: pool.cdt0,
      cdt1: pool.cdt1,
      ...(pool.host ? { host: pool.host } : {}),
      members: pool.members.map((member) => member.cdt1),
    }));
    for (const carve of carves) {
      if (!(carve.parentCdt0 in sectionOfCdt0)) {
        sectionOfCdt0[carve.parentCdt0] = sectionOfCdt0[carve.cdt0] ?? carve.parentCdt0;
      }
      if (!(carve.parentCdt0 in cdt1AttrOfCdt0)) {
        cdt1AttrOfCdt0[carve.parentCdt0] = cdt1AttrOfCdt0[carve.cdt0] ?? "";
      }
    }
    return {
      cdt0Order: pairs.cdt0Order,

      // A folded carve can revive its parent even when the base ships only its children.
      frozenCdt0Names: [
        ...new Set([
          ...pairs.cdt0Order,
          ...shippedCdt0s,
          ...carves.map((carve) => carve.parentCdt0),
        ]),
      ],
      cdt1AttrOfCdt0,
      sectionOfCdt0,
      pairOrder: rawPairs.pairs.map((pair) => ({ cdt0: pair.cdt0, cdt1: pair.cdt1 })),
      nodes: ruleNodes,
      skus,
      basePools,
      pooling: {
        subFloorShare: SUB_FLOOR_SHARE,

        // Mirrors Pogger's minimum viable width of 2.5 widest-pack facings.
        subFloorFacings: 2.5,
        oneSku: true,
        topExempt: TOP_SALES_EXEMPT,
        cap: POOL_CAP,
      },
      carves,
      // Unitless end to end (Wendy, 2026-10-29): every length here is raw planogram units.
      lengthUnit: "units" as const,
      baseShelfCm: (runs[0]?.bays ?? 0) * (runs[0]?.bayWidthCm ?? 0) * shelfSpace.shelfRanks.length,
      partition: zoneCandidates,
      // One source: the wall inference read, which the whole Finalize already follows.
      // Written only when horizontal, so a vertical wall's rule set stays develop's.
      ...(isHorizontalWall(draft) ? { wallBlocking: "horizontal" as const } : {}),
      // Only a horizontal wall's choice other than the default ships (DEV-3531).
      ...(isHorizontalWall(draft) && draft?.brandShelves
        ? { brandShelves: draft.brandShelves }
        : {}),
      ...(options.fences ? { fences: options.fences } : {}),
      strata: emittedStrata.map(emitted.stratum),
      baseDemandCm: Object.fromEntries(baseDemandCmOf),
      minDemandCm: Object.fromEntries(minDemandCmOf),
      rowHomes: emitted.rowHomes([...preRowHomes, ...privateRowHomes, ...rowHomes]),
    };
  })();

  return {
    tree: { items: orderedTreeItems, cdt0Order: pairs.cdt0Order, segments: pairs.pairs },
    restrictions: {
      rules: {
        partition: partitionRules,
        anchorRule: "the first bay of each zone anchors its own strata",
      },
      rows,
      anchors,
      partition: zonedPartition,
      rowStrata,
      bay1Anchors,
      zoneSeams,
      rowHomeStrata: homed.rowHomeStrata.map(emitted.stratum),
    },
    pins,
    orderingRules: ruleReports,
    rowHomes: emitted.rowHomes([...preRowHomes, ...privateRowHomes, ...rowHomes]),
    pools,
    poolViability,
    rules,
    advisories,
    findings,
  };
};
